<template>
  <div ref="containerRef" class="particle-ring-container">
    <!-- Logo 图片 - 从下往上滚动出来 -->
    <div v-if="showLogoImage" class="logo-container">
      <div
        class="logo-wrapper"
        :style="{
          clipPath: `inset(${100 - logoRevealPercent}% 0 0 0)`,
          opacity: logoOpacity,
        }"
      >
        <img :src="logoImage" alt="Logo" class="logo-image" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import * as THREE from 'three'

// 导入图片
import logoImage from '@/assets/images/animation/logo.png'

// Props - 控制是否允许内部点击
const props = withDefaults(
  defineProps<{
    disableInternalClick?: boolean
  }>(),
  {
    disableInternalClick: false,
  },
)

const emit = defineEmits<{
  (e: 'moleculeClick'): void
  (e: 'hoverChange', isHovering: boolean): void
}>()

const containerRef = ref<HTMLElement | null>(null)
let isHovering = false // 跟踪hover状态

// 移动端检测
const isMobile =
  typeof window !== 'undefined' &&
  (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  ) ||
    window.innerWidth < 768)

// 性能配置 - 移动端适度减少粒子数量，保证性能同时保持效果
const config = {
  // 图片采样间隔 (移动端每2像素采样，粒子减少约75%)
  imageSamplingStep: isMobile ? 2 : 1,
  // Logo 采样间隔
  logoSamplingStep: isMobile ? 2 : 1,
  // 圆圈模式背景粒子数量
  circleBgParticleCount: isMobile ? 4000 : 3000,
  // 分子模式背景粒子数量 - 更少一些
  moleculeBgParticleCount: isMobile ? 1000 : 3000,
  // 城市模式背景粒子数量
  bgParticleCount: isMobile ? 2500 : 4000,
  // 圆圈粒子数量 - 增加数量
  circleParticleCount: isMobile ? 800 : 1000,
  // 转场目标粒子数量 (移动端减少50%)
  transitionParticleCount: isMobile ? 15000 : 30000,
}

// 获取实际的 devicePixelRatio
const actualPixelRatio =
  typeof window !== 'undefined' ? window.devicePixelRatio : 1

// 强制使用 pixelRatio=2 进行渲染，确保所有设备都有相同的渲染质量
const fixedPixelRatio = 2

// 计算视口尺寸缩放因子的函数
// 基准分辨率为 1512（效果好的分辨率宽度）
// 当屏幕更大时，需要放大粒子以保持相同的视觉效果
const baseWidth = 1512
function getParticleSizeScale() {
  const viewportWidth =
    typeof window !== 'undefined' ? window.innerWidth : baseWidth
  return Math.max(viewportWidth / baseWidth, 1.0)
}

// 粒子大小缩放因子 - 初始值，会在 init 时更新
let particleSizeScale = 1.0

// sizeScale 用于其他粒子效果
const pixelRatioScale = 1.0

let scene: THREE.Scene
let camera: THREE.PerspectiveCamera
let renderer: THREE.WebGLRenderer
let particles: THREE.Points
let logoParticles: THREE.Points | null = null
let animationId: number

// 粒子相关变量
let particleCount = 0
let mouseX = 0
let mouseY = 0
let mouse3D = new THREE.Vector3()

// 当前显示的形状：'circle' 或 'molecule' 或 'city' 或 'ai'
let currentShape = 'circle'

// 圆圈相关
const circleParticleCount = config.circleParticleCount
const circleRadius = isMobile ? 7 : 11 // 正圆半径
let circleFloatOffset = 0
let circleComplete = false // 圆圈是否已完成
// 圆圈粒子的目标位置和初始扩散位置
let circleTargetPositions: { x: number; y: number; z: number }[] = []
let circleScatterPositions: { x: number; y: number; z: number }[] = []
// 粒子飞入起始位置（屏幕外）
const circleStartPositions: { x: number; y: number; z: number }[] = []
// 动画时间相关
let circleInitTime = 0 // 初始化时间
const circleFlyInDuration = 1200 // 飞入汇聚动画时间（毫秒）

// 分子相关
let moleculeTargets: THREE.Vector3[] = []
let moleculeColors: THREE.Color[] = []
// 粒子额外属性：距离中心比例(0-1)和是否是特殊原子(0或1)
const moleculeIntensities: number[] = []
const moleculeSpecials: number[] = []

// 城市相关
let cityTargets: THREE.Vector3[] = []
let cityColors: THREE.Color[] = []
let cityParticles: THREE.Points | null = null
let groundParticles: THREE.Points | null = null
let groundTargets: THREE.Vector3[] = []
let isCityConverging = false
let cityConvergeStartTime = 0
const cityConvergeDuration = 2500
let cityRotationX = -0.1 // 更平的视角
let cityRotationY = 0.3
let cityScale = 0.9
const cityMinScale = 1.0
const cityMaxScale = 2.5

// 城市粒子汇聚动画的初始位置
let cityConvergeStartPositions: { x: number; y: number; z: number }[] = []
let groundConvergeStartPositions: { x: number; y: number; z: number }[] = []

// AI球体相关
let aiSphereTargets: THREE.Vector3[] = []
let aiSphereColors: THREE.Color[] = []
let aiTextTargets: THREE.Vector3[] = []
let aiTextColors: THREE.Color[] = []
let aiSphereParticles: THREE.Points | null = null
let aiTextParticles: THREE.Points | null = null
let aiGroup: THREE.Group | null = null
let aiTextGroup: THREE.Group | null = null // AI文字单独的Group，用于浮动和拖动旋转
let aiRotationX = 0
let aiRotationY = 0
let aiTextRotationX = 0 // AI文字的旋转角度
let aiTextRotationY = 0
let aiScale = 1.0
const aiMinScale = 0.8
const aiMaxScale = 2.0
let isAiConverging = false
let aiConvergeStartTime = 0
const aiConvergeDuration = 2000
let aiAutoRotationActive = false
let aiFormationComplete = false
let aiRotationSpeed = 0 // 当前旋转速度，用于平滑启动
const aiTargetRotationSpeed = 0.003 // 目标旋转速度
// AI球体汇聚动画的初始位置
let aiSphereConvergeStartPositions: { x: number; y: number; z: number }[] = []
let aiTextConvergeStartPositions: { x: number; y: number; z: number }[] = []

// 预计算的城市粒子数据（在初始化时计算，但不渲染）
let preCityParticles: THREE.Points | null = null
let preGroundParticles: THREE.Points | null = null
let preCityBgParticles: THREE.Points | null = null
let cityDataInitialized = false
// 城市背景粒子的目标位置（用于浮动效果）
let cityBgTargetPositions: { x: number; y: number; z: number }[] = []

// 3D分子结构配置 - 使用原来的圆圈颜色
const MOLECULE_COLORS = [
  new THREE.Color(0x003a8a),
  new THREE.Color(0x30b6eb),
  new THREE.Color(0x7ec3ab),
  new THREE.Color(0x004296),
]

// 分子原子配置
const moleculeAtoms = [
  // 核心六元环 - 拉伸透视
  {
    id: 'r1',
    position: new THREE.Vector3(-5, 10, 0),
    size: 3.8,
    colorIndex: 0,
  },
  {
    id: 'r2',
    position: new THREE.Vector3(6, 8, 0),
    size: 3.5,
    colorIndex: 1,
  },
  {
    id: 'r3',
    position: new THREE.Vector3(8, -2, 0),
    size: 4.0,
    colorIndex: 2,
  },
  {
    id: 'r4',
    position: new THREE.Vector3(0, -12, 0),
    size: 3.8,
    colorIndex: 3,
  },
  {
    id: 'r5',
    position: new THREE.Vector3(-15, -10, 0),
    size: 4.2,
    colorIndex: 0,
  },
  {
    id: 'r6',
    position: new THREE.Vector3(-14, 2, 0),
    size: 4.0,
    colorIndex: 1,
  },
  // 右上延伸长链
  {
    id: 't1',
    position: new THREE.Vector3(14, 18, -2),
    size: 3.5,
    colorIndex: 2,
  },
  {
    id: 't2',
    position: new THREE.Vector3(9, 20, 5),
    size: 3.0,
    colorIndex: 0,
  },
  // 右下角巨大的高亮原子 (视觉焦点)
  {
    id: 'b1',
    position: new THREE.Vector3(5, -21, 2),
    size: 3.0,
    colorIndex: 1,
  },
  // 左上/侧方延伸
  {
    id: 'w1',
    position: new THREE.Vector3(-30, 2, -1),
    size: 5.2,
    colorIndex: 3,
  },
]

// 分子键配置
const moleculeBonds = [
  // 环连接
  { startId: 'r1', endId: 'r2' },
  { startId: 'r2', endId: 'r3' },
  { startId: 'r3', endId: 'r4' },
  { startId: 'r4', endId: 'r5' },
  { startId: 'r5', endId: 'r6' },
  { startId: 'r6', endId: 'r1' },
  // 长链连接
  { startId: 'r2', endId: 't1' },
  { startId: 't1', endId: 't2' },
  { startId: 'r4', endId: 'b1' },
  { startId: 'r6', endId: 'w1' },
]

// Logo 相关 - 使用 HTML 图片展示
const showLogoImage = ref(false)
const logoRevealPercent = ref(0) // 从下往上显示的百分比 (0-100)
const logoOpacity = ref(0)
let logoAnimationId: number | null = null
const logoAnimationDuration = 800 // logo 动画时长（毫秒）

// 旧的粒子 logo 相关变量（保留但不再使用）
const logoTargets: THREE.Vector3[] = []
const logoColors: THREE.Color[] = []
let showLogo = false
const logoGrowthProgress = 0
let logoMaxRadius = 0
const logoStartTime = 0
const logoGrowthDuration = 800

// 第三张图片相关
const thirdImageTargets: THREE.Vector3[] = []
const thirdImageColors: THREE.Color[] = []

// 第四张图片相关
const fourthImageTargets: THREE.Vector3[] = []
const fourthImageColors: THREE.Color[] = []

// 当前显示的是第几张图
let currentImageIndex = 3

// 转场动画
let isTransitioning = false
let transitionState = 'idle'
let scatterTargets: { x: number; y: number; z: number }[] = []
let scatterStartPositions: { x: number; y: number; z: number }[] = []
let transitionProgress = 0

// 长按效果相关
let isMouseDown = false
let mouseDownTime = 0
let clickEffectActive = false
let clickEffectProgress = 0
const clickPosition = new THREE.Vector3()
let originalPositions: { x: number; y: number; z: number }[] = []

// 鼠标移动流动效果
const mouseMoveVelocity = { x: 0, y: 0 }

// 保存原始尺寸
let originalSizes: number[] = []

// 分子结构 Group（用于360度旋转）
let moleculeGroup: THREE.Group | null = null
// 背景粒子（不旋转）
let bgParticles: THREE.Points | null = null
// 背景粒子目标位置（用于汇聚动画）
let bgTargetPositions: { x: number; y: number; z: number }[] = []
// 分子拖动旋转相关
let isDragging = false
const previousMousePosition = { x: 0, y: 0 }
// 初始倾斜角度（向左上方倾斜）
let moleculeRotationX = -0.3 // 向上倾斜
let moleculeRotationY = -0.4 // 向左倾斜
// 分子缩放
let moleculeScale = 1.0
const minScale = 1.0
const maxScale = 2.5

// 自动循环旋转相关
let autoRotationActive = false
let moleculeFormationComplete = false
const autoRotationSpeed = 0.0006 // 旋转速度（弧度/帧），较慢的速度
let convergeStartTime = 0 // 汇聚开始时间

// 分子渐变色配置 - 简化为4个主色，使用平滑插值
const MOLECULE_GRADIENT_COLORS = [
  new THREE.Color(0x8acbb2), // 最内侧 - 浅绿
  new THREE.Color(0x47b897), // 绿色
  new THREE.Color(0x37beef), // 青蓝
  new THREE.Color(0x004296), // 最外侧 - 深蓝
]

// 创建发光粒子纹理 - 和恒星一样的效果
function createParticleTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = 64
  canvas.height = 64
  const ctx = canvas.getContext('2d')
  if (ctx) {
    const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32)
    grad.addColorStop(0, 'rgba(255, 255, 255, 1)')
    grad.addColorStop(0.15, 'rgba(255, 255, 255, 0.9)')
    grad.addColorStop(0.3, 'rgba(255, 255, 255, 0.6)')
    grad.addColorStop(0.5, 'rgba(255, 255, 255, 0.3)')
    grad.addColorStop(0.7, 'rgba(255, 255, 255, 0.1)')
    grad.addColorStop(1, 'rgba(255, 255, 255, 0)')
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, 64, 64)
  }
  return new THREE.CanvasTexture(canvas)
}

// 缓存纹理
let particleTexture: THREE.CanvasTexture | null = null
function getParticleTexture(): THREE.CanvasTexture {
  if (!particleTexture) {
    particleTexture = createParticleTexture()
  }
  return particleTexture
}

function init() {
  if (!containerRef.value) return

  // 在客户端初始化时更新 particleSizeScale
  particleSizeScale = getParticleSizeScale()

  const container = containerRef.value
  const width = container.clientWidth
  const height = container.clientHeight

  scene = new THREE.Scene()
  camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000)

  // 创建渲染器，禁用自动像素比调整
  renderer = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: 'high-performance',
  })

  // 手动设置 canvas 尺寸为 CSS 尺寸的 2 倍，确保高清渲染
  const renderWidth = width * fixedPixelRatio
  const renderHeight = height * fixedPixelRatio
  renderer.setSize(renderWidth, renderHeight, false)
  renderer.domElement.style.width = width + 'px'
  renderer.domElement.style.height = height + 'px'
  renderer.setClearColor(0xffffff, 1)
  container.appendChild(renderer.domElement)

  camera.position.z = 20
  // 移动端相机往上移动，让圆圈显示在屏幕下半部分
  camera.position.y = isMobile ? -5 : 0
  // 相机看向下方，让内容显示在屏幕下半部分
  camera.lookAt(0, isMobile ? 3 : 0, 0)

  // 预渲染城市：在创建圆圈粒子之前执行，确保 WebGL 资源正确初始化
  // 这必须在 animate() 被调用之前执行
  preRenderCity()

  generate3DMoleculeShape()
  loadLogoShape()
}

function generate3DMoleculeShape() {
  // 每个原子的粒子数量 - 减少数量
  const particlesPerAtom = isMobile ? 4000 : 8000
  // 键的粒子数量 - 减少数量
  const particlesPerBond = isMobile ? 1000 : 2000

  // 缩放因子，调整整体大小
  const scaleFactor = isMobile ? 0.3 : 0.45

  // 所有原子都使用毛边效果
  const fuzzyAtomIds = [
    'r1',
    'r2',
    'r3',
    'r4',
    'r5',
    'r6',
    't1',
    't2',
    'b1',
    'w1',
  ]

  // 预计算每个原子的缩放后半径
  const atomRadii: { [key: string]: number } = {}
  for (const atom of moleculeAtoms) {
    atomRadii[atom.id] = atom.size * scaleFactor
  }

  // 辅助函数：根据距离中心的比例获取渐变色（HSL连续插值）
  function getGradientColor(intensity: number): THREE.Color {
    // intensity: 0 = 中心, 1 = 边缘
    // 增加随机扰动让颜色更融合
    const noise = (Math.random() - 0.5) * 0.25
    const t = Math.max(0, Math.min(1, intensity + noise))

    // 在 HSL 空间中进行连续插值
    // 内层 #8ACBB2: H=156, S=40%, L=67%
    // 外层 #004296: H=216, S=100%, L=29%
    // 色相从 156 度（青绿）渐变到 216 度（蓝色）
    const hue = 156 + t * 60
    // 饱和度从 40% 渐变到 100%
    const saturation = 0.4 + t * 0.6
    // 亮度从 67% 渐变到 29%
    const lightness = 0.67 - t * 0.38

    const resultColor = new THREE.Color()
    resultColor.setHSL(hue / 360, saturation, lightness)
    return resultColor
  }

  // 生成原子（3D球形分布）- 边缘不光滑
  for (const atom of moleculeAtoms) {
    const atomRadius = atomRadii[atom.id]
    const atomCenterX = atom.position.x * scaleFactor
    const atomCenterY = atom.position.y * scaleFactor
    const isFuzzy = fuzzyAtomIds.includes(atom.id)

    // 所有原子都使用毛边效果
    const maxRadiusRatio = 1.05

    for (let i = 0; i < particlesPerAtom; i++) {
      // 使用均匀体积分布
      const randomVal = Math.random()

      // 毛边效果：边缘有随机扩散
      const fuzzyFactor = 1 + Math.random() * 0.08
      const r = Math.pow(randomVal, 1 / 3) * atomRadius * fuzzyFactor

      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)

      // 正确的球坐标转换 - 确保是正圆
      const localX = r * Math.sin(phi) * Math.cos(theta)
      const localY = r * Math.sin(phi) * Math.sin(theta)
      const localZ = r * Math.cos(phi)

      const x = localX + atomCenterX
      const y = localY + atomCenterY
      const z = localZ + atom.position.z * scaleFactor

      moleculeTargets.push(new THREE.Vector3(x, y, z))

      // 根据距离中心的比例获取渐变色
      // 对于光滑原子，将颜色映射到完整范围
      const normalizedR = r / (atomRadius * maxRadiusRatio)
      const intensity = Math.min(normalizedR, 1)
      const particleColor = getGradientColor(intensity)
      moleculeColors.push(particleColor)

      // 记录粒子属性
      moleculeIntensities.push(intensity)
      moleculeSpecials.push(isFuzzy ? 1.0 : 0.0)
    }
  }

  // 生成键（圆柱形粒子分布）- 棱角分明，直接从原子边缘开始
  for (const bond of moleculeBonds) {
    const startAtom = moleculeAtoms.find((a) => a.id === bond.startId)
    const endAtom = moleculeAtoms.find((a) => a.id === bond.endId)

    if (!startAtom || !endAtom) continue

    const startCenter = startAtom.position.clone().multiplyScalar(scaleFactor)
    const endCenter = endAtom.position.clone().multiplyScalar(scaleFactor)

    // 计算方向向量
    const fullDirection = endCenter.clone().sub(startCenter)
    const fullLength = fullDirection.length()
    const direction = fullDirection.clone().normalize()

    // 获取两个原子的半径
    const startRadius = atomRadii[startAtom.id]
    const endRadius = atomRadii[endAtom.id]

    // 链的起点在原子边缘（棱角分明）
    const bondStart = startCenter
      .clone()
      .add(direction.clone().multiplyScalar(startRadius * 0.85))
    const bondEnd = endCenter
      .clone()
      .sub(direction.clone().multiplyScalar(endRadius * 0.85))

    // 实际链的长度
    const bondLength = bondStart.distanceTo(bondEnd)

    // 键的半径 - 更宽的链
    const bondRadius = 1 * scaleFactor
    // 链的最大半径比例 - 增加让边缘不光滑
    const bondMaxRadiusRatio = 1.08

    // 创建垂直于direction的两个正交向量
    const up = new THREE.Vector3(0, 1, 0)
    if (Math.abs(direction.dot(up)) > 0.9) {
      up.set(1, 0, 0)
    }
    const perp1 = new THREE.Vector3().crossVectors(direction, up).normalize()
    const perp2 = new THREE.Vector3().crossVectors(direction, perp1).normalize()

    for (let i = 0; i < particlesPerBond; i++) {
      // 沿着键的方向分布 - 使用更均匀的分布
      const t = Math.random()
      const basePos = bondStart
        .clone()
        .add(direction.clone().multiplyScalar(bondLength * t))

      // 在垂直于键方向的圆形截面上分布 - 添加随机扩散让边缘不光滑
      const perpAngle = Math.random() * Math.PI * 2
      // 添加随机扩散
      const fuzzyFactor = 1 + Math.random() * 0.1
      const perpDist =
        Math.sqrt(Math.random()) * bondRadius * bondMaxRadiusRatio * fuzzyFactor

      const offset = perp1
        .clone()
        .multiplyScalar(Math.cos(perpAngle) * perpDist)
        .add(perp2.clone().multiplyScalar(Math.sin(perpAngle) * perpDist))

      const pos = basePos.add(offset)
      moleculeTargets.push(pos)

      // 键的颜色：使用渐变色，根据到圆柱中心的距离，限制最大值避免黑色
      const rawDist = perpDist / (bondRadius * bondMaxRadiusRatio)
      const normalizedDist = Math.min(rawDist, 0.85)
      const bondColor = getGradientColor(normalizedDist)
      moleculeColors.push(bondColor)

      // 键的粒子属性：不是特殊原子，强度为距离比例
      moleculeIntensities.push(normalizedDist)
      moleculeSpecials.push(0.0)
    }
  }

  // eslint-disable-next-line no-console
  console.log(`生成了 ${moleculeTargets.length} 个3D分子粒子`)
  createCircleParticles()
}

// function loadThirdImageShape() {
//   const img = new Image()
//   img.crossOrigin = 'anonymous'
//   img.onload = function () {
//     const canvas = document.createElement('canvas')
//     const ctx = canvas.getContext('2d')!

//     const maxWidth = 1200
//     const scale = maxWidth / img.width
//     canvas.width = img.width * scale
//     canvas.height = img.height * scale

//     ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

//     const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)

//     const aspect = window.innerWidth / window.innerHeight
//     const vFov = (75 * Math.PI) / 180
//     const viewHeight = 2 * Math.tan(vFov / 2) * 15
//     const viewWidth = viewHeight * aspect

//     const imgAspect = canvas.width / canvas.height
//     let scaleX: number, scaleY: number

//     if (imgAspect > aspect) {
//       scaleY = (viewHeight * 1) / canvas.height
//       scaleX = scaleY
//     } else {
//       scaleX = (viewWidth * 1) / canvas.width
//       scaleY = scaleX
//     }

//     const topColor = new THREE.Color(0x0154bb)
//     const bottomColor = new THREE.Color(0x9bffde)

//     let minY = Infinity
//     let maxY = -Infinity

//     const step = config.imageSamplingStep
//     for (let y = 0; y < canvas.height; y += step) {
//       for (let x = 0; x < canvas.width; x += step) {
//         const index = (y * canvas.width + x) * 4
//         const a = imageData.data[index + 3]

//         if (a > 50) {
//           const posY = -(y - canvas.height / 2) * scaleY
//           if (posY < minY) minY = posY
//           if (posY > maxY) maxY = posY
//         }
//       }
//     }

//     const yRange = maxY - minY

//     for (let y = 0; y < canvas.height; y += step) {
//       for (let x = 0; x < canvas.width; x += step) {
//         const index = (y * canvas.width + x) * 4
//         const a = imageData.data[index + 3]

//         if (a > 50) {
//           const posY = -(y - canvas.height / 2) * scaleY

//           thirdImageTargets.push(
//             new THREE.Vector3((x - canvas.width / 2) * scaleX, posY, 0),
//           )

//           const t = (posY - minY) / yRange
//           const color = new THREE.Color()
//           color.lerpColors(bottomColor, topColor, t)
//           thirdImageColors.push(color)
//         }
//       }
//     }

//     console.log(`从第三张图片加载了 ${thirdImageTargets.length} 个粒子`)
//   }
//   img.onerror = function () {
//     console.error('无法加载 ceshi.png')
//   }
//   img.src = ceshiImage
// }

// function loadFourthImageShape() {
//   const img = new Image()
//   img.crossOrigin = 'anonymous'
//   img.onload = function () {
//     const canvas = document.createElement('canvas')
//     const ctx = canvas.getContext('2d')!

//     const maxWidth = 1200
//     const scale = maxWidth / img.width
//     canvas.width = img.width * scale
//     canvas.height = img.height * scale

//     ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

//     const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)

//     const aspect = window.innerWidth / window.innerHeight
//     const vFov = (75 * Math.PI) / 180
//     const viewHeight = 2 * Math.tan(vFov / 2) * 15
//     const viewWidth = viewHeight * aspect

//     const imgAspect = canvas.width / canvas.height
//     let scaleX: number, scaleY: number

//     if (imgAspect > aspect) {
//       scaleY = (viewHeight * 1) / canvas.height
//       scaleX = scaleY
//     } else {
//       scaleX = (viewWidth * 1) / canvas.width
//       scaleY = scaleX
//     }

//     const step = config.imageSamplingStep
//     for (let y = 0; y < canvas.height; y += step) {
//       for (let x = 0; x < canvas.width; x += step) {
//         const index = (y * canvas.width + x) * 4
//         const r = imageData.data[index]
//         const g = imageData.data[index + 1]
//         const b = imageData.data[index + 2]
//         const a = imageData.data[index + 3]

//         if (a > 50) {
//           fourthImageTargets.push(
//             new THREE.Vector3(
//               (x - canvas.width / 2) * scaleX,
//               -(y - canvas.height / 2) * scaleY,
//               0,
//             ),
//           )
//           fourthImageColors.push(new THREE.Color(r / 255, g / 255, b / 255))
//         }
//       }
//     }

//     console.log(`从第四张图片加载了 ${fourthImageTargets.length} 个粒子`)
//   }
//   img.onerror = function () {
//     console.error('无法加载 ceshi1.png')
//   }
//   img.src = ceshi1Image
// }

function loadLogoShape() {
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.onload = function () {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!

    const maxWidth = 400
    const scale = maxWidth / img.width
    canvas.width = img.width * scale
    canvas.height = img.height * scale

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)

    const logoScale = 0.025
    const samplingStep = config.logoSamplingStep

    for (let y = 0; y < canvas.height; y += samplingStep) {
      for (let x = 0; x < canvas.width; x += samplingStep) {
        const index = (y * canvas.width + x) * 4
        const r = imageData.data[index]
        const g = imageData.data[index + 1]
        const b = imageData.data[index + 2]
        const a = imageData.data[index + 3]

        if (a < 50) continue

        const isWhite = r > 200 && g > 200 && b > 200
        if (isWhite && Math.random() > 0.98) continue

        const isGray =
          Math.abs(r - g) < 30 && Math.abs(g - b) < 30 && r > 50 && r < 200
        if (isGray && Math.random() > 0.995) continue

        const posX = (x - canvas.width / 2) * logoScale
        const posY = -(y - canvas.height / 2) * logoScale

        logoTargets.push(new THREE.Vector3(posX, posY, 0))
        logoColors.push(new THREE.Color(r / 255, g / 255, b / 255))

        const distToCenter = Math.sqrt(posX * posX + posY * posY)
        if (distToCenter > logoMaxRadius) {
          logoMaxRadius = distToCenter
        }
      }
    }
  }
  img.onerror = function () {
    // Logo 加载失败
  }
  img.src = logoImage
}

// 生成AI球体粒子数据
function generateAiSphereData() {
  aiSphereTargets = []
  aiSphereColors = []
  aiTextTargets = []
  aiTextColors = []

  const sphereRadius = isMobile ? 6 : 8
  const sphereParticleCount = isMobile ? 20000 : 35000
  const textParticleCount = isMobile ? 2000 : 3000

  // AI球体颜色 - 8种颜色混合
  const gradientColors = [
    new THREE.Color(0x37beef), // 青蓝
    new THREE.Color(0x5fcbf2), // 浅青蓝
    new THREE.Color(0x9bdef7), // 淡蓝
    new THREE.Color(0xd7f2fc), // 极淡蓝
    new THREE.Color(0x47b897), // 绿色
    new THREE.Color(0x6cc6ac), // 浅绿
    new THREE.Color(0xa3dbcb), // 淡绿
    new THREE.Color(0xdaf1ea), // 极淡绿
  ]

  // 3D空心球壳效果：粒子只分布在球的外层薄壳上
  // 球壳的内半径和外半径 - 内半径更大，让中心更空
  const shellInnerRadius = sphereRadius * 0.95 // 球壳内边界（更靠外）
  const shellOuterRadius = sphereRadius * 1.2 // 球壳外边界
  const shellThickness = shellOuterRadius - shellInnerRadius
  const shellCenter = (shellInnerRadius + shellOuterRadius) / 2

  // 正面的洞通过shader动态隐藏，这里生成完整的球壳粒子
  for (let i = 0; i < sphereParticleCount; i++) {
    // 球面均匀分布角度
    const theta = Math.random() * Math.PI * 2
    const phi = Math.acos(2 * Math.random() - 1)

    // 半径分布：使用高斯分布让粒子集中在球壳中心
    const u1 = Math.random()
    const u2 = Math.random()
    const gaussian = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
    const stdDev = shellThickness * 0.15
    let r = shellCenter + gaussian * stdDev

    // 严格限制在球壳范围内
    r = Math.max(shellInnerRadius, Math.min(shellOuterRadius, r))

    const x = r * Math.sin(phi) * Math.cos(theta)
    const y = r * Math.sin(phi) * Math.sin(theta)
    const z = r * Math.cos(phi)

    aiSphereTargets.push(new THREE.Vector3(x, y, z))

    // 随机选择8种颜色之一
    const color =
      gradientColors[Math.floor(Math.random() * gradientColors.length)].clone()

    aiSphereColors.push(color)
  }

  // 生成"AI"文字粒子
  generateAiTextParticles(textParticleCount)

  // eslint-disable-next-line no-console
  console.log(
    `生成了 ${aiSphereTargets.length} 个球体粒子和 ${aiTextTargets.length} 个文字粒子`,
  )
}

// 生成AI文字粒子
function generateAiTextParticles(count: number) {
  // 使用 Canvas 绘制 "AI" 文字并采样
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!

  canvas.width = 256
  canvas.height = 128

  // 清空画布
  ctx.fillStyle = 'black'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // 绘制 AI 文字
  ctx.fillStyle = 'white'
  ctx.font = 'bold 100px Arial'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('AI', canvas.width / 2, canvas.height / 2)

  // 获取像素数据
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const pixels: { x: number; y: number }[] = []

  // 采样白色像素
  for (let y = 0; y < canvas.height; y += 2) {
    for (let x = 0; x < canvas.width; x += 2) {
      const index = (y * canvas.width + x) * 4
      if (imageData.data[index] > 128) {
        pixels.push({ x, y })
      }
    }
  }

  // 从采样点生成3D粒子
  const textScale = isMobile ? 0.065 : 0.08
  const textDepth = isMobile ? 1.5 : 2.0

  // AI文字颜色 - 8种颜色混合
  const textColors = [
    new THREE.Color(0x37beef), // 青蓝
    new THREE.Color(0x5fcbf2), // 浅青蓝
    new THREE.Color(0x9bdef7), // 淡蓝
    new THREE.Color(0xd7f2fc), // 极淡蓝
    new THREE.Color(0x47b897), // 绿色
    new THREE.Color(0x6cc6ac), // 浅绿
    new THREE.Color(0xa3dbcb), // 淡绿
    new THREE.Color(0xdaf1ea), // 极淡绿
  ]

  const particlesPerPixel = Math.ceil(count / pixels.length)

  for (const pixel of pixels) {
    for (let i = 0; i < particlesPerPixel; i++) {
      // 添加随机偏移让文字更有体积感
      const offsetX = (Math.random() - 0.5) * 3
      const offsetY = (Math.random() - 0.5) * 3
      const offsetZ = (Math.random() - 0.5) * textDepth

      const x = (pixel.x - canvas.width / 2 + offsetX) * textScale
      const y = -(pixel.y - canvas.height / 2 + offsetY) * textScale
      const z = offsetZ

      aiTextTargets.push(new THREE.Vector3(x, y, z))

      // 随机选择一个颜色
      const baseColor =
        textColors[Math.floor(Math.random() * textColors.length)]
      const color = baseColor.clone()
      // 轻微的亮度变化让文字更有层次感
      const brightness = 0.9 + Math.random() * 0.2
      color.multiplyScalar(brightness)

      aiTextColors.push(color)
    }
  }
}

function createCircleParticles() {
  const positions: number[] = []
  const colors: number[] = []
  const sizes: number[] = []

  const circleColors = [
    // 8种混合颜色
    new THREE.Color(0x37beef), // 青蓝
    new THREE.Color(0x5fcbf2), // 浅青蓝
    new THREE.Color(0x9bdef7), // 淡蓝
    new THREE.Color(0xd7f2fc), // 极淡蓝
    new THREE.Color(0x47b897), // 绿色
    new THREE.Color(0x6cc6ac), // 浅绿
    new THREE.Color(0xa3dbcb), // 淡绿
    new THREE.Color(0xdaf1ea), // 极淡绿
  ]

  particleCount = circleParticleCount

  // 计算视口范围用于扩散
  const aspect = window.innerWidth / window.innerHeight
  const vFov = (75 * Math.PI) / 180
  const viewHeight = 2 * Math.tan(vFov / 2) * 20
  const viewWidth = viewHeight * aspect

  // 预先计算所有粒子的目标位置（椭圆圈）和初始扩散位置
  // 清空所有位置数组，确保每次创建都是全新的
  circleTargetPositions = []
  circleScatterPositions = []
  circleStartPositions.length = 0 // 清空飞入起始位置数组

  for (let i = 0; i < particleCount; i++) {
    const seed = i * 12.9898
    const inCluster = i % 20 < 12

    // 计算圆圈上的目标位置
    let radiusVariation = 1.0
    if (inCluster) {
      radiusVariation = 0.85 + (Math.sin(seed * 3.456) * 0.5 + 0.5) * 0.15
    } else {
      radiusVariation = 0.95 + (Math.cos(seed * 4.567) * 0.5 + 0.5) * 0.1
    }

    const currentRadius = circleRadius * radiusVariation
    const offsetRadius = (Math.sin(seed) * 0.5 + 0.5 - 0.5) * 0.5
    const finalRadius = currentRadius + offsetRadius

    // 均匀分布在圆圈上
    const progress = i / particleCount
    const angle = progress * Math.PI * 2
    const offsetAngle = (Math.cos(seed * 1.234) * 0.5 + 0.5 - 0.5) * 0.1
    const finalAngle = angle + offsetAngle

    // 正圆形：X 和 Y 使用相同的半径
    const targetX = Math.cos(finalAngle) * finalRadius
    const targetY = Math.sin(finalAngle) * finalRadius
    const targetZ = Math.sin(seed * 2.345) * 0.3

    circleTargetPositions.push({ x: targetX, y: targetY, z: targetZ })

    // 计算初始扩散位置（从圆心向外扩散）
    const scatterDistance = 15 + Math.random() * 20
    const scatterAngle = Math.random() * Math.PI * 2
    const scatterX =
      Math.cos(scatterAngle) * scatterDistance + (Math.random() - 0.5) * 5
    const scatterY =
      Math.sin(scatterAngle) * scatterDistance + (Math.random() - 0.5) * 5
    const scatterZ = (Math.random() - 0.5) * 10

    circleScatterPositions.push({ x: scatterX, y: scatterY, z: scatterZ })

    // 计算飞入起始位置（从目标位置的延长线方向飞入，避免螺旋感）
    const flyInDistance = 40 + Math.random() * 30
    // 使用与目标位置相同的角度，让粒子沿直线飞入
    const startX = Math.cos(finalAngle) * flyInDistance
    const startY = Math.sin(finalAngle) * flyInDistance
    const startZ = (Math.random() - 0.5) * 20

    circleStartPositions.push({ x: startX, y: startY, z: startZ })

    // 初始位置设为飞入起始位置（屏幕外）
    positions.push(startX, startY, startZ)

    // 8种颜色等分：根据粒子索引选择颜色，确保每种颜色数量相等
    const colorIndex = i % circleColors.length
    const color = circleColors[colorIndex]
    colors.push(color.r, color.g, color.b)

    const sizeRandom = Math.random()
    if (sizeRandom < 0.1) sizes.push(2.0 + Math.random() * 1.5)
    else if (sizeRandom < 0.25) sizes.push(1.2 + Math.random() * 0.8)
    else sizes.push(0.4 + Math.random() * 0.8)
  }

  originalSizes = [...sizes]

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3),
  )
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))
  geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1))

  const material = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute float size;
      attribute vec3 color;

      varying vec3 vColor;

      void main() {
        vColor = color;

        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * 150.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        // 圆形裁剪，更清晰的边缘
        if (dist > 0.45) discard;

        // 实心圆，边缘稍微柔和但不模糊
        float alpha = 1.0 - smoothstep(0.35, 0.45, dist);

        gl_FragColor = vec4(vColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  particles = new THREE.Points(geometry, material)
  scene.add(particles)

  // 创建背景粒子（和分子结构图一样）
  createCircleBackgroundParticles(viewWidth, viewHeight)

  // 记录初始化时间，用于定格效果
  circleInitTime = Date.now()

  animate()
}

// 创建圆圈模式的背景粒子
function createCircleBackgroundParticles(
  viewWidth: number,
  viewHeight: number,
) {
  const bgColors = [
    new THREE.Color(0x639ee9),
    new THREE.Color(0x5fcbf2),
    new THREE.Color(0x6cc6ac),
    new THREE.Color(0xa1d5c1),
  ]

  const bgParticleCount = config.circleBgParticleCount
  const bgPositions: number[] = []
  const bgColorsArr: number[] = []
  const bgSizes: number[] = []
  bgTargetPositions = []

  // 横向占满全屏，增大分布范围
  // 桌面端和移动端都使用更大的横向范围确保占满屏幕
  const bgSpreadMultiplierX = isMobile ? 5.0 : 3.0
  const bgSpreadMultiplierY = isMobile ? 2.5 : 1.8

  for (let i = 0; i < bgParticleCount; i++) {
    // 目标位置：最终要到达的位置，横向占满全屏
    const targetX = (Math.random() - 0.5) * viewWidth * bgSpreadMultiplierX
    const targetY = (Math.random() - 0.5) * viewHeight * bgSpreadMultiplierY
    const targetZ = -3 - Math.random() * 7
    bgTargetPositions.push({ x: targetX, y: targetY, z: targetZ })

    // 初始位置：从屏幕外随机位置开始飞入
    const flyInAngle = Math.random() * Math.PI * 2
    const flyInDistance = 30 + Math.random() * 40
    bgPositions.push(
      Math.cos(flyInAngle) * flyInDistance + (Math.random() - 0.5) * 10,
      Math.sin(flyInAngle) * flyInDistance + (Math.random() - 0.5) * 10,
      -10 - Math.random() * 10,
    )

    const bgColor = bgColors[Math.floor(Math.random() * bgColors.length)]
    bgColorsArr.push(bgColor.r, bgColor.g, bgColor.b)

    // 随机大小：大小差距更明显，更参差不齐
    const sizeRand = Math.random()
    if (sizeRand < 0.5) {
      bgSizes.push(0.04 + Math.random() * 0.08) // 很小的粒子
    } else if (sizeRand < 0.75) {
      bgSizes.push(0.15 + Math.random() * 0.15) // 小粒子
    } else if (sizeRand < 0.9) {
      bgSizes.push(0.35 + Math.random() * 0.25) // 中粒子
    } else if (sizeRand < 0.97) {
      bgSizes.push(0.6 + Math.random() * 0.5) // 大粒子
    } else {
      bgSizes.push(1.0 + Math.random() * 0.9) // 特大粒子
    }
  }

  const bgGeometry = new THREE.BufferGeometry()
  bgGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(bgPositions, 3),
  )
  bgGeometry.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(bgColorsArr, 3),
  )
  bgGeometry.setAttribute('size', new THREE.Float32BufferAttribute(bgSizes, 1))

  // 背景粒子使用和分子模式一样的 shader
  const bgMaterial = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
      time: { value: 0 },
      clickPos: { value: new THREE.Vector3() },
      clickProgress: { value: 0 },
      clickActive: { value: 0 },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute vec3 color;
      attribute float size;
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform vec3 clickPos;
      uniform float clickProgress;
      uniform float clickActive;

      void main() {
        vColor = color;
        vPosition = position;

        vDistToClick = distance(position, clickPos);

        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * 300.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform float clickProgress;
      uniform float clickActive;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        // 更锐利的边缘，提高清晰度
        float alpha = 1.0 - smoothstep(0.3, 0.5, dist);

        vec3 finalColor = vColor;

        if (clickActive > 0.5) {
          float effectRadius = 5.0;
          float influence = 1.0 - clamp(vDistToClick / effectRadius, 0.0, 1.0);

          if (clickProgress < 1.0) {
            float brightness = 1.0 + influence * clickProgress * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * clickProgress * 0.6) * brightness;
          } else {
            float fadeProgress = clickProgress - 1.0;
            float brightness = 1.0 + influence * (1.0 - fadeProgress) * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * (1.0 - fadeProgress) * 0.6) * brightness;
          }
        }

        gl_FragColor = vec4(finalColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  bgParticles = new THREE.Points(bgGeometry, bgMaterial)
  scene.add(bgParticles)
}

function switchToFourthImage() {
  currentShape = 'molecule'

  moleculeTargets = [...fourthImageTargets]
  moleculeColors = [...fourthImageColors]

  const bgColors = [
    new THREE.Color(0x639ee9),
    new THREE.Color(0x5fcbf2),
    new THREE.Color(0x6cc6ac),
    new THREE.Color(0xa1d5c1),
  ]

  const bgParticleCount = config.bgParticleCount
  const newParticleCount = moleculeTargets.length + bgParticleCount

  const newPositions: number[] = []
  const newColors: number[] = []

  const aspect = window.innerWidth / window.innerHeight
  const vFov = (camera.fov * Math.PI) / 180
  const height = 2 * Math.tan(vFov / 2) * 15
  const width = height * aspect

  for (let i = 0; i < newParticleCount; i++) {
    if (i < moleculeTargets.length) {
      if (i < scatterTargets.length) {
        const scatter = scatterTargets[i]
        newPositions.push(scatter.x, scatter.y, scatter.z)
      } else {
        newPositions.push(
          (Math.random() - 0.5) * width * 1.2,
          (Math.random() - 0.5) * height * 1.2,
          (Math.random() - 0.5) * 2,
        )
      }
      const color = moleculeColors[i]
      newColors.push(color.r, color.g, color.b)
    } else {
      newPositions.push(
        (Math.random() - 0.5) * width * 1.3,
        (Math.random() - 0.5) * height * 1.3,
        -3 - Math.random() * 7,
      )
      const bgColor = bgColors[Math.floor(Math.random() * bgColors.length)]
      newColors.push(bgColor.r, bgColor.g, bgColor.b)
    }
  }

  scene.remove(particles)

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(newPositions, 3),
  )
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(newColors, 3))

  const material = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
      time: { value: 0 },
      clickPos: { value: new THREE.Vector3() },
      clickProgress: { value: 0 },
      clickActive: { value: 0 },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute vec3 color;
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform vec3 clickPos;
      uniform float clickProgress;
      uniform float clickActive;

      void main() {
        vColor = color;
        vPosition = position;

        vDistToClick = distance(position, clickPos);

        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = 0.10 * 300.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform float clickProgress;
      uniform float clickActive;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        // 更锐利的边缘，提高清晰度
        float alpha = 1.0 - smoothstep(0.3, 0.5, dist);

        vec3 finalColor = vColor;

        if (clickActive > 0.5) {
          float effectRadius = 5.0;
          float influence = 1.0 - clamp(vDistToClick / effectRadius, 0.0, 1.0);

          if (clickProgress < 1.0) {
            float brightness = 1.0 + influence * clickProgress * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * clickProgress * 0.6) * brightness;
          } else {
            float fadeProgress = clickProgress - 1.0;
            float brightness = 1.0 + influence * (1.0 - fadeProgress) * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * (1.0 - fadeProgress) * 0.6) * brightness;
          }
        }

        gl_FragColor = vec4(finalColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  particles = new THREE.Points(geometry, material)
  scene.add(particles)

  particleCount = newParticleCount
}

function switchToThirdImage() {
  currentShape = 'molecule'

  moleculeTargets = [...thirdImageTargets]
  moleculeColors = [...thirdImageColors]

  const bgColors = [
    new THREE.Color(0x639ee9),
    new THREE.Color(0x5fcbf2),
    new THREE.Color(0x6cc6ac),
    new THREE.Color(0xa1d5c1),
  ]

  const bgParticleCount = config.bgParticleCount
  const newParticleCount = moleculeTargets.length + bgParticleCount

  const newPositions: number[] = []
  const newColors: number[] = []

  const aspect = window.innerWidth / window.innerHeight
  const vFov = (camera.fov * Math.PI) / 180
  const height = 2 * Math.tan(vFov / 2) * 15
  const width = height * aspect

  for (let i = 0; i < newParticleCount; i++) {
    if (i < moleculeTargets.length) {
      if (i < scatterTargets.length) {
        const scatter = scatterTargets[i]
        newPositions.push(scatter.x, scatter.y, scatter.z)
      } else {
        newPositions.push(
          (Math.random() - 0.5) * width * 1.2,
          (Math.random() - 0.5) * height * 1.2,
          (Math.random() - 0.5) * 2,
        )
      }
      const color = moleculeColors[i]
      newColors.push(color.r, color.g, color.b)
    } else {
      newPositions.push(
        (Math.random() - 0.5) * width * 1.3,
        (Math.random() - 0.5) * height * 1.3,
        -3 - Math.random() * 7,
      )
      const bgColor = bgColors[Math.floor(Math.random() * bgColors.length)]
      newColors.push(bgColor.r, bgColor.g, bgColor.b)
    }
  }

  scene.remove(particles)

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(newPositions, 3),
  )
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(newColors, 3))

  const material = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
      time: { value: 0 },
      clickPos: { value: new THREE.Vector3() },
      clickProgress: { value: 0 },
      clickActive: { value: 0 },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute vec3 color;
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform vec3 clickPos;
      uniform float clickProgress;
      uniform float clickActive;

      void main() {
        vColor = color;
        vPosition = position;

        vDistToClick = distance(position, clickPos);

        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = 0.10 * 300.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform float clickProgress;
      uniform float clickActive;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        // 更锐利的边缘，提高清晰度
        float alpha = 1.0 - smoothstep(0.3, 0.5, dist);

        vec3 finalColor = vColor;

        if (clickActive > 0.5) {
          float effectRadius = 5.0;
          float influence = 1.0 - clamp(vDistToClick / effectRadius, 0.0, 1.0);

          if (clickProgress < 1.0) {
            float brightness = 1.0 + influence * clickProgress * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * clickProgress * 0.6) * brightness;
          } else {
            float fadeProgress = clickProgress - 1.0;
            float brightness = 1.0 + influence * (1.0 - fadeProgress) * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * (1.0 - fadeProgress) * 0.6) * brightness;
          }
        }

        gl_FragColor = vec4(finalColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  particles = new THREE.Points(geometry, material)
  scene.add(particles)

  particleCount = newParticleCount
}

function switchToMolecule() {
  currentShape = 'molecule'
  currentImageIndex = 2

  // 记录汇聚开始时间
  convergeStartTime = Date.now()
  // 重置旋转状态
  moleculeFormationComplete = false
  autoRotationActive = false

  // eslint-disable-next-line no-console
  console.log(`切换到分子形状，粒子数量: ${moleculeTargets.length}`)

  // 先移除旧的背景粒子
  if (bgParticles) {
    scene.remove(bgParticles)
    // 只有当 bgParticles 不是预渲染的城市背景粒子时才 dispose
    if (bgParticles !== preCityBgParticles) {
      if (bgParticles.geometry) bgParticles.geometry.dispose()
      if (bgParticles.material)
        (bgParticles.material as THREE.Material).dispose()
    }
    bgParticles = null
  }

  const bgColors = [
    new THREE.Color(0x639ee9),
    new THREE.Color(0x5fcbf2),
    new THREE.Color(0x6cc6ac),
    new THREE.Color(0xa1d5c1),
  ]

  const bgParticleCount = config.moleculeBgParticleCount
  const newParticleCount = moleculeTargets.length + bgParticleCount

  const newPositions: number[] = []
  const newColors: number[] = []
  const newIntensities: number[] = []
  const newSpecials: number[] = []

  const aspect = window.innerWidth / window.innerHeight
  const vFov = (camera.fov * Math.PI) / 180
  const height = 2 * Math.tan(vFov / 2) * 15
  const width = height * aspect

  // 分子粒子 - 初始位置从扩散后的位置开始（如果有的话）
  for (let i = 0; i < moleculeTargets.length; i++) {
    // 优先使用扩散后的位置，否则随机分散
    if (scatterTargets.length > i) {
      newPositions.push(
        scatterTargets[i].x,
        scatterTargets[i].y,
        scatterTargets[i].z,
      )
    } else {
      newPositions.push(
        (Math.random() - 0.5) * width * 1.5,
        (Math.random() - 0.5) * height * 1.5,
        (Math.random() - 0.5) * 10,
      )
    }
    const color = moleculeColors[i]
    newColors.push(color.r, color.g, color.b)
    newIntensities.push(moleculeIntensities[i])
    newSpecials.push(moleculeSpecials[i])
  }

  // 移除旧的粒子
  scene.remove(particles)

  // 创建分子粒子
  const moleculeGeometry = new THREE.BufferGeometry()
  moleculeGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(newPositions, 3),
  )
  moleculeGeometry.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(newColors, 3),
  )
  moleculeGeometry.setAttribute(
    'intensity',
    new THREE.Float32BufferAttribute(newIntensities, 1),
  )
  moleculeGeometry.setAttribute(
    'special',
    new THREE.Float32BufferAttribute(newSpecials, 1),
  )

  // 使用 ShaderMaterial，模拟发光效果
  const moleculeMaterial = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute vec3 color;
      attribute float intensity;
      varying vec3 vColor;
      varying float vIntensity;

      void main() {
        vColor = color;
        vIntensity = intensity;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = 0.13 * 300.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying float vIntensity;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        // 更强的中心发光效果
        float glow = 1.0 - smoothstep(0.0, 0.45, dist);
        glow = pow(glow, 1.0);
        
        // 根据粒子在球体中的位置调整发光强度
        // 越靠近球心的粒子发光越强
        float centerBoost = 1.0 - vIntensity;
        centerBoost = pow(centerBoost, 0.6);
        
        // 混合白色产生发光效果，大幅增强
        float glowStrength = glow * (0.7 + centerBoost * 0.5);
        vec3 glowColor = mix(vColor, vec3(1.0), glowStrength);
        
        // 整体提亮更多
        glowColor = glowColor * (1.0 + centerBoost * 0.35);
        
        // 柔和的边缘透明度
        float alpha = 1.0 - smoothstep(0.05, 0.5, dist);
        alpha = pow(alpha, 0.6);
        
        gl_FragColor = vec4(glowColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  particles = new THREE.Points(moleculeGeometry, moleculeMaterial)

  // 创建分子 Group 用于旋转
  moleculeGroup = new THREE.Group()
  moleculeGroup.add(particles)
  scene.add(moleculeGroup)

  // 创建背景粒子（不旋转）- 从扩散位置开始汇聚
  const bgPositions: number[] = []
  const bgColorsArr: number[] = []
  const bgSizes: number[] = []
  bgTargetPositions = [] // 重置背景粒子目标位置

  // 移动端需要更大的分布范围来覆盖整个屏幕
  // 与圆圈背景粒子保持一致的分布范围
  const bgSpreadMultiplierX = isMobile ? 5.0 : 3.0
  const bgSpreadMultiplierY = isMobile ? 2.5 : 1.8

  for (let i = 0; i < bgParticleCount; i++) {
    // 目标位置：最终要到达的位置
    const targetX = (Math.random() - 0.5) * width * bgSpreadMultiplierX
    const targetY = (Math.random() - 0.5) * height * bgSpreadMultiplierY
    const targetZ = -3 - Math.random() * 7
    bgTargetPositions.push({ x: targetX, y: targetY, z: targetZ })

    // 初始位置：从扩散位置开始（复用圆圈粒子的扩散位置，或随机生成）
    const scatterIndex = i % Math.max(scatterTargets.length, 1)
    if (scatterTargets.length > 0) {
      const scatter = scatterTargets[scatterIndex]
      // 在扩散位置基础上添加一些随机偏移
      bgPositions.push(
        scatter.x + (Math.random() - 0.5) * 5,
        scatter.y + (Math.random() - 0.5) * 5,
        scatter.z + (Math.random() - 0.5) * 5,
      )
    } else {
      // 如果没有扩散位置，从屏幕外开始
      bgPositions.push(
        (Math.random() - 0.5) * width * 2,
        (Math.random() - 0.5) * height * 2,
        -10 - Math.random() * 10,
      )
    }

    const bgColor = bgColors[Math.floor(Math.random() * bgColors.length)]
    bgColorsArr.push(bgColor.r, bgColor.g, bgColor.b)

    // 随机大小：与圆圈背景粒子保持一致，但大粒子和特大粒子数量减少
    const sizeRand = Math.random()
    if (sizeRand < 0.6) {
      bgSizes.push(0.03 + Math.random() * 0.1) // 很小的粒子
    } else if (sizeRand < 0.85) {
      bgSizes.push(0.08 + Math.random() * 0.15) // 小粒子
    } else if (sizeRand < 0.95) {
      bgSizes.push(0.16 + Math.random() * 0.25) // 中粒子
    } else {
      bgSizes.push(0.28 + Math.random() * 0.4) // 大粒子
    }
  }

  const bgGeometry = new THREE.BufferGeometry()
  bgGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(bgPositions, 3),
  )
  bgGeometry.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(bgColorsArr, 3),
  )
  bgGeometry.setAttribute('size', new THREE.Float32BufferAttribute(bgSizes, 1))

  // 背景粒子使用和原来一样的 shader
  const bgMaterial = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
      time: { value: 0 },
      clickPos: { value: new THREE.Vector3() },
      clickProgress: { value: 0 },
      clickActive: { value: 0 },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute vec3 color;
      attribute float size;
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform vec3 clickPos;
      uniform float clickProgress;
      uniform float clickActive;

      void main() {
        vColor = color;
        vPosition = position;

        vDistToClick = distance(position, clickPos);

        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * 300.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform float clickProgress;
      uniform float clickActive;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        // 更锐利的边缘，提高清晰度
        float alpha = 1.0 - smoothstep(0.3, 0.5, dist);

        vec3 finalColor = vColor;

        if (clickActive > 0.5) {
          float effectRadius = 5.0;
          float influence = 1.0 - clamp(vDistToClick / effectRadius, 0.0, 1.0);

          if (clickProgress < 1.0) {
            float brightness = 1.0 + influence * clickProgress * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * clickProgress * 0.6) * brightness;
          } else {
            float fadeProgress = clickProgress - 1.0;
            float brightness = 1.0 + influence * (1.0 - fadeProgress) * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * (1.0 - fadeProgress) * 0.6) * brightness;
          }
        }

        gl_FragColor = vec4(finalColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  bgParticles = new THREE.Points(bgGeometry, bgMaterial)
  scene.add(bgParticles)

  particleCount = moleculeTargets.length
  // 桌面端相机距离更近，让图案更大
  // 移动端相机往上移动，让分子结构图显示在屏幕下半部分
  camera.position.z = isMobile ? 15 : 12
  camera.position.y = isMobile ? -5 : 0

  // 启动动画循环
  animate()
}

// 城市颜色 - 从下往上渐变：#37BEEF -> #47B897
function getCityColor(yPercent: number) {
  const bottomColor = new THREE.Color(0x9bdef7) // 底部 - 青蓝
  const topColor = new THREE.Color(0xa3dbcb) // 顶部 - 绿色
  const c = new THREE.Color()
  c.lerpColors(bottomColor, topColor, yPercent)
  return c
}

// 存储建筑物信息用于背景粒子避让
interface BuildingInfo {
  x: number
  z: number
  width: number
  depth: number
  height: number
  type: 'box' | 'cylinder'
  radius?: number
}
let cityBuildings: BuildingInfo[] = []

// 检查某个位置是否在建筑物区域内（包括上方）
// 注意：城市粒子有缩放 cityScale，背景粒子需要考虑这个缩放
function isInsideBuilding(
  x: number,
  y: number,
  z: number,
  scale: number = 1.0,
): boolean {
  // 将背景粒子坐标转换到城市的本地坐标系（考虑缩放）
  // 背景粒子在世界坐标，城市粒子被缩放了，所以需要反向缩放来比较
  const localX = x / scale
  const localY = y / scale
  const localZ = z / scale

  for (const building of cityBuildings) {
    if (building.type === 'cylinder') {
      // 圆柱建筑
      const dx = localX - building.x
      const dz = localZ - building.z
      const dist = Math.sqrt(dx * dx + dz * dz)
      // 增加更大的边距，让背景粒子离建筑物更远
      const margin = 8
      if (
        dist < (building.radius || 2) + margin &&
        localY >= -5 &&
        localY <= building.height + 20
      ) {
        return true
      }
    } else {
      // 方形建筑
      const halfW = building.width / 2 + 5 // 增加更大的边距
      const halfD = building.depth / 2 + 5
      if (
        localX >= building.x - halfW &&
        localX <= building.x + halfW &&
        localZ >= building.z - halfD &&
        localZ <= building.z + halfD &&
        localY >= -5 &&
        localY <= building.height + 20
      ) {
        return true
      }
    }
  }
  return false
}

// 生成城市粒子数据
function generateCityData() {
  cityTargets = []
  cityColors = []
  groundTargets = []
  cityBuildings = [] // 清空建筑物信息

  const seededRandom = (seed: number) => {
    const x = Math.sin(seed) * 10000
    return x - Math.floor(x)
  }

  // 中心塔楼
  const h = 50
  // 记录中心塔楼信息 - 使用更大的范围来避免背景粒子出现在上方
  cityBuildings.push({
    x: 0,
    z: 0,
    width: 10,
    depth: 10,
    height: h,
    type: 'box',
  })

  for (let y = 0; y <= h * 0.6; y += 0.4) {
    const c = getCityColor(y / h)
    const w = 3
    for (let lx = -w; lx <= w; lx += 0.35) {
      cityTargets.push(new THREE.Vector3(lx, y, -w))
      cityTargets.push(new THREE.Vector3(lx, y, w))
      cityColors.push(c.clone(), c.clone())
    }
    for (let lz = -w; lz <= w; lz += 0.35) {
      cityTargets.push(new THREE.Vector3(-w, y, lz))
      cityTargets.push(new THREE.Vector3(w, y, lz))
      cityColors.push(c.clone(), c.clone())
    }
  }
  for (let y = h * 0.6; y <= h * 0.85; y += 0.4) {
    const yp = (y - h * 0.6) / (h * 0.25)
    const w = 3 - yp * 1.5
    const c = getCityColor(y / h)
    for (let lx = -w; lx <= w; lx += 0.35) {
      cityTargets.push(new THREE.Vector3(lx, y, -w))
      cityTargets.push(new THREE.Vector3(lx, y, w))
      cityColors.push(c.clone(), c.clone())
    }
    for (let lz = -w; lz <= w; lz += 0.35) {
      cityTargets.push(new THREE.Vector3(-w, y, lz))
      cityTargets.push(new THREE.Vector3(w, y, lz))
      cityColors.push(c.clone(), c.clone())
    }
  }
  for (let y = h * 0.85; y <= h; y += 0.25) {
    const yp = (y - h * 0.85) / (h * 0.15)
    const r = 1 * (1 - yp)
    const c = getCityColor(0.9)
    for (let a = 0; a < Math.PI * 2; a += 0.4) {
      cityTargets.push(new THREE.Vector3(Math.cos(a) * r, y, Math.sin(a) * r))
      cityColors.push(c.clone())
    }
  }

  // 圆柱建筑
  const cylinders = [
    { x: -15, z: 10, r: 2, h: 35 },
    { x: 12, z: -8, r: 1.8, h: 40 },
  ]
  // 记录圆柱建筑信息
  for (const cyl of cylinders) {
    cityBuildings.push({
      x: cyl.x,
      z: cyl.z,
      width: 0,
      depth: 0,
      height: cyl.h,
      type: 'cylinder',
      radius: cyl.r,
    })
  }
  for (const cyl of cylinders) {
    for (let y = 0; y <= cyl.h; y += 0.5) {
      const c = getCityColor(y / cyl.h)
      for (let a = 0; a < Math.PI * 2; a += 0.15) {
        cityTargets.push(
          new THREE.Vector3(
            cyl.x + Math.cos(a) * cyl.r,
            y,
            cyl.z + Math.sin(a) * cyl.r,
          ),
        )
        cityColors.push(c.clone())
      }
    }
  }

  // 密集建筑群
  for (let i = 0; i < 100; i++) {
    const seed = i * 7
    const angle = seededRandom(seed) * Math.PI * 2
    const dist = 8 + seededRandom(seed + 1) * 25
    const bx = Math.cos(angle) * dist
    const bz = Math.sin(angle) * dist

    if (Math.abs(bx) < 6 && Math.abs(bz) < 6) continue
    if (Math.abs(bx + 15) < 4 && Math.abs(bz - 10) < 4) continue
    if (Math.abs(bx - 12) < 4 && Math.abs(bz + 8) < 4) continue

    const bh = 8 + seededRandom(seed + 2) * 20 * (1 - dist / 50)
    const bw = 1.5 + seededRandom(seed + 3) * 1.5
    const bd = 1.5 + seededRandom(seed + 4) * 1.5

    // 记录建筑物信息
    cityBuildings.push({
      x: bx,
      z: bz,
      width: bw,
      depth: bd,
      height: bh,
      type: 'box',
    })

    for (let y = 0; y <= bh; y += 0.5) {
      const c = getCityColor(y / bh)
      for (let lx = -bw / 2; lx <= bw / 2; lx += 0.4) {
        cityTargets.push(new THREE.Vector3(bx + lx, y, bz - bd / 2))
        cityTargets.push(new THREE.Vector3(bx + lx, y, bz + bd / 2))
        cityColors.push(c.clone(), c.clone())
      }
      for (let lz = -bd / 2; lz <= bd / 2; lz += 0.4) {
        cityTargets.push(new THREE.Vector3(bx - bw / 2, y, bz + lz))
        cityTargets.push(new THREE.Vector3(bx + bw / 2, y, bz + lz))
        cityColors.push(c.clone(), c.clone())
      }
    }
  }

  // 地面
  const size = 80
  const groundC = new THREE.Color().setHSL(0.54, 0.6, 0.75)
  for (let x = -size; x <= size; x += 2) {
    for (let z = -size; z <= size; z += 2) {
      groundTargets.push(new THREE.Vector3(x, 0, z))
    }
  }
  // 网格线
  for (let x = -size; x <= size; x += 8) {
    for (let z = -size; z <= size; z += 1) {
      groundTargets.push(new THREE.Vector3(x, 0, z))
    }
  }
  for (let z = -size; z <= size; z += 8) {
    for (let x = -size; x <= size; x += 1) {
      groundTargets.push(new THREE.Vector3(x, 0, z))
    }
  }
}

// 预渲染城市：在初始化时执行一次完整的城市渲染流程
// 这样可以确保所有 WebGL 资源（着色器、纹理等）都被正确初始化
function preRenderCity() {
  if (cityDataInitialized) return

  // 生成城市数据
  generateCityData()

  // 保存当前相机位置
  const savedCameraPos = camera.position.clone()
  const savedCameraZ = camera.position.z
  const savedCameraY = camera.position.y

  // 城市的默认缩放值
  const defaultCityScale = isMobile ? 0.6 : 0.9

  // 创建城市背景粒子
  const bgColors = [
    new THREE.Color(0x639ee9),
    new THREE.Color(0x5fcbf2),
    new THREE.Color(0x6cc6ac),
    new THREE.Color(0xa1d5c1),
  ]
  const bgPositions: number[] = []
  const bgColorsArr: number[] = []
  const bgSizes: number[] = []
  const bgCount = config.bgParticleCount
  cityBgTargetPositions = [] // 清空并重新生成

  for (let i = 0; i < bgCount; i++) {
    let x: number, y: number, z: number
    let attempts = 0
    const maxAttempts = 50

    // 尝试生成不在建筑物区域内的位置
    // 城市模式下，背景粒子主要分布在：
    // 1. 城市外围（距离中心较远的位置）
    // 2. 地面以下（Y < 0）
    // 3. 城市建筑物之间的空隙
    do {
      // 随机决定粒子的分布区域
      const regionChoice = Math.random()

      if (regionChoice < 0.4) {
        // 40% 的粒子分布在城市外围（距离中心 > 50）
        const angle = Math.random() * Math.PI * 2
        const dist = 50 + Math.random() * 50
        x = Math.cos(angle) * dist
        z = Math.sin(angle) * dist
        y = Math.random() * 60 - 10
      } else if (regionChoice < 0.7) {
        // 30% 的粒子分布在地面以下
        x = (Math.random() - 0.5) * 200
        z = (Math.random() - 0.5) * 200
        y = -5 - Math.random() * 25
      } else {
        // 30% 的粒子尝试分布在城市区域内，但避开建筑物
        x = (Math.random() - 0.5) * 100
        z = (Math.random() - 0.5) * 100
        y = Math.random() * 40 - 10
      }
      attempts++
    } while (
      isInsideBuilding(x, y, z, defaultCityScale) &&
      attempts < maxAttempts
    )

    // 如果多次尝试后仍在建筑物内，将粒子移到城市外围或地面以下
    if (isInsideBuilding(x, y, z, defaultCityScale)) {
      // 将粒子移到城市外围
      const angle = Math.random() * Math.PI * 2
      const dist = 60 + Math.random() * 40
      x = Math.cos(angle) * dist
      z = Math.sin(angle) * dist
      y = Math.random() * 40 - 20
    }

    bgPositions.push(x, y, z)
    cityBgTargetPositions.push({ x, y, z }) // 保存目标位置

    const bgColor = bgColors[Math.floor(Math.random() * bgColors.length)]
    bgColorsArr.push(bgColor.r, bgColor.g, bgColor.b)

    // 随机大小：城市模式背景粒子尺寸适中，最大粒子不要太大
    const sizeRand = Math.random()
    // if (sizeRand < 0.5) {
    //   bgSizes.push(0.05 + Math.random() * 0.08) // 小粒子
    // } else if (sizeRand < 0.75) {
    //   bgSizes.push(0.12 + Math.random() * 0.1) // 中小粒子
    // } else if (sizeRand < 0.9) {
    //   bgSizes.push(0.2 + Math.random() * 0.12) // 中粒子
    // } else if (sizeRand < 0.97) {
    //   bgSizes.push(0.28 + Math.random() * 0.12) // 大粒子
    // } else {
    //   bgSizes.push(0.35 + Math.random() * 0.15) // 特大粒子（减小最大尺寸）
    // }
  }

  const bgGeometry = new THREE.BufferGeometry()
  bgGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(bgPositions, 3),
  )
  bgGeometry.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(bgColorsArr, 3),
  )
  bgGeometry.setAttribute('size', new THREE.Float32BufferAttribute(bgSizes, 1))

  const bgMaterial = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute vec3 color;
      attribute float size;
      varying vec3 vColor;

      void main() {
        vColor = color;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * 300.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        // 圆形裁剪，更小的半径让边缘更锐利
        if (dist > 0.4) discard;

        // 实心圆点，边缘锐利无毛边
        float alpha = 1.0 - smoothstep(0.3, 0.4, dist);

        gl_FragColor = vec4(vColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  preCityBgParticles = new THREE.Points(bgGeometry, bgMaterial)

  // 创建城市粒子
  const cityPositions: number[] = []
  const cityColorsArr: number[] = []

  for (let i = 0; i < cityTargets.length; i++) {
    const target = cityTargets[i]
    cityPositions.push(target.x, target.y, target.z)
    const c = cityColors[i] || getCityColor(0.5)
    cityColorsArr.push(c.r, c.g, c.b)
  }

  const cityGeo = new THREE.BufferGeometry()
  cityGeo.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(cityPositions, 3),
  )
  cityGeo.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(cityColorsArr, 3),
  )

  const cityMat = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: particleSizeScale },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute vec3 color;
      varying vec3 vColor;

      void main() {
        vColor = color;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        float depth = -mvPosition.z;
        
        float baseSize = 4.0 * sizeScale;
        gl_PointSize = baseSize * (50.0 / depth);
        gl_PointSize = clamp(gl_PointSize, 1.5, 15.0);
        
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        float alpha = 0.95 * (1.0 - smoothstep(0.3, 0.5, dist));

        gl_FragColor = vec4(vColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  preCityParticles = new THREE.Points(cityGeo, cityMat)
  preCityParticles.rotation.x = cityRotationX
  preCityParticles.rotation.y = cityRotationY
  preCityParticles.scale.setScalar(isMobile ? 0.6 : 0.9)

  // 创建地面粒子
  const groundPositions: number[] = []
  const groundColorsArr: number[] = []
  const groundC = new THREE.Color().setHSL(0.54, 0.6, 0.75)
  const lineC = new THREE.Color().setHSL(0.54, 0.7, 0.65)

  for (let i = 0; i < groundTargets.length; i++) {
    const target = groundTargets[i]
    groundPositions.push(target.x, target.y, target.z)
    const c = i < groundTargets.length * 0.3 ? groundC : lineC
    groundColorsArr.push(c.r, c.g, c.b)
  }

  const groundGeo = new THREE.BufferGeometry()
  groundGeo.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(groundPositions, 3),
  )
  groundGeo.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(groundColorsArr, 3),
  )

  const groundMat = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: particleSizeScale },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute vec3 color;
      varying vec3 vColor;

      void main() {
        vColor = color;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        float depth = -mvPosition.z;
        
        float baseSize = 4.5 * sizeScale;
        gl_PointSize = baseSize * (50.0 / depth);
        gl_PointSize = clamp(gl_PointSize, 1.5, 18.0);
        
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        float alpha = 0.9 * (1.0 - smoothstep(0.3, 0.5, dist));

        gl_FragColor = vec4(vColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  preGroundParticles = new THREE.Points(groundGeo, groundMat)
  preGroundParticles.rotation.x = cityRotationX
  preGroundParticles.rotation.y = cityRotationY
  preGroundParticles.scale.setScalar(isMobile ? 0.6 : 0.9)

  // 关键步骤：将粒子添加到场景，设置城市相机，渲染多帧
  scene.add(preCityBgParticles)
  scene.add(preCityParticles)
  scene.add(preGroundParticles)

  // 设置城市相机位置
  if (isMobile) {
    camera.position.set(0, 35, 55)
    camera.lookAt(0, 5, 0)
  } else {
    camera.position.set(0, 25, 55)
    camera.lookAt(0, 10, 0)
  }
  camera.updateProjectionMatrix()

  // 渲染多帧以确保 WebGL 完全初始化所有资源（着色器、纹理、缓冲区等）
  // 增加到 10 帧以确保完全初始化
  for (let i = 0; i < 10; i++) {
    renderer.render(scene, camera)
  }

  // 不移除城市粒子，只是将它们设为不可见
  // 这样可以保持 WebGL 资源的状态
  preCityBgParticles.visible = false
  preCityParticles.visible = false
  preGroundParticles.visible = false

  // 恢复相机位置
  camera.position.z = savedCameraZ
  camera.position.y = savedCameraY
  camera.position.x = 0
  camera.lookAt(0, isMobile ? 3 : 0, 0)
  camera.updateProjectionMatrix()

  cityDataInitialized = true
  // eslint-disable-next-line no-console
  console.log('城市预渲染完成')
}

// 切换到城市
function switchToCity() {
  currentShape = 'city'
  isTransitioning = false

  // 移动端城市缩小一点
  cityScale = isMobile ? 0.6 : 0.9

  // 移除旧粒子
  if (particles) scene.remove(particles)
  if (moleculeGroup) {
    scene.remove(moleculeGroup)
    moleculeGroup = null
  }
  // 移除旧背景粒子
  if (bgParticles) {
    scene.remove(bgParticles)
    // 只有当 bgParticles 不是预渲染的城市背景粒子时才 dispose
    if (bgParticles !== preCityBgParticles) {
      if (bgParticles.geometry) bgParticles.geometry.dispose()
      if (bgParticles.material)
        (bgParticles.material as THREE.Material).dispose()
    }
    bgParticles = null
  }

  // 确保预计算数据已初始化
  if (!cityDataInitialized) {
    preRenderCity()
  }

  // 使用预计算的粒子，设置为可见
  if (preCityBgParticles) {
    bgParticles = preCityBgParticles
    bgParticles.visible = true
  }

  // 清空并重新生成初始位置数组
  cityConvergeStartPositions = []
  groundConvergeStartPositions = []

  if (preCityParticles) {
    cityParticles = preCityParticles
    cityParticles.visible = true

    // 更新 uniform 的 sizeScale 值
    const cityMaterial = cityParticles.material as THREE.ShaderMaterial
    if (cityMaterial.uniforms && cityMaterial.uniforms.sizeScale) {
      cityMaterial.uniforms.sizeScale.value = particleSizeScale
    }

    const cityPos = cityParticles.geometry.attributes.position
      .array as Float32Array

    // 设置初始扩散位置并保存
    for (let i = 0; i < cityTargets.length; i++) {
      const angle = Math.random() * Math.PI * 2
      const dist = 30 + Math.random() * 50
      const x = Math.cos(angle) * dist
      const y = Math.random() * 30 - 15
      const z = Math.sin(angle) * dist
      cityPos[i * 3] = x
      cityPos[i * 3 + 1] = y
      cityPos[i * 3 + 2] = z
      cityConvergeStartPositions.push({ x, y, z })
    }
    cityParticles.geometry.attributes.position.needsUpdate = true

    cityParticles.rotation.x = cityRotationX
    cityParticles.rotation.y = cityRotationY
    cityParticles.scale.setScalar(cityScale)
  }

  if (preGroundParticles) {
    groundParticles = preGroundParticles
    groundParticles.visible = true

    // 更新 uniform 的 sizeScale 值
    const groundMaterial = groundParticles.material as THREE.ShaderMaterial
    if (groundMaterial.uniforms && groundMaterial.uniforms.sizeScale) {
      groundMaterial.uniforms.sizeScale.value = particleSizeScale
    }

    const groundPos = groundParticles.geometry.attributes.position
      .array as Float32Array

    // 设置初始扩散位置并保存
    for (let i = 0; i < groundTargets.length; i++) {
      const angle = Math.random() * Math.PI * 2
      const dist = 50 + Math.random() * 80
      const x = Math.cos(angle) * dist
      const y = Math.random() * 20 - 10
      const z = Math.sin(angle) * dist
      groundPos[i * 3] = x
      groundPos[i * 3 + 1] = y
      groundPos[i * 3 + 2] = z
      groundConvergeStartPositions.push({ x, y, z })
    }
    groundParticles.geometry.attributes.position.needsUpdate = true

    groundParticles.rotation.x = cityRotationX
    groundParticles.rotation.y = cityRotationY
    groundParticles.scale.setScalar(cityScale)
  }

  // 调整相机 - 更近更大
  // 移动端相机往上移动，让城市显示在屏幕下半部分
  if (isMobile) {
    camera.position.set(0, 35, 55)
    camera.lookAt(0, 5, 0)
  } else {
    camera.position.set(0, 25, 55)
    camera.lookAt(0, 10, 0)
  }

  // 强制更新相机投影矩阵
  camera.updateProjectionMatrix()

  // 开始汇聚动画
  isCityConverging = true
  cityConvergeStartTime = Date.now()

  // 启动动画循环
  animate()
}

// 城市动画
function animateCity() {
  const time = Date.now() * 0.001
  const now = Date.now()

  if (!cityParticles || !groundParticles) return

  // 城市扩散动画
  if (isCityScattering) {
    const elapsed = now - cityScatterStartTime
    const duration = 1200 // 与圆圈扩散时间一致
    let progress = Math.min(elapsed / duration, 1)
    // 使用缓出效果，开始快后面慢（与圆圈扩散一致）
    progress = 1 - Math.pow(1 - progress, 2.5)

    // 城市粒子扩散
    const cityPos = cityParticles.geometry.attributes.position
      .array as Float32Array
    for (let i = 0; i < cityScatterStartPositions.length; i++) {
      const start = cityScatterStartPositions[i]
      const target = cityScatterTargets[i]
      const idx = i * 3

      cityPos[idx] = start.x + (target.x - start.x) * progress
      cityPos[idx + 1] = start.y + (target.y - start.y) * progress
      cityPos[idx + 2] = start.z + (target.z - start.z) * progress

      // 添加浮动效果
      const floatSpeed = 0.8 + (i % 10) * 0.08
      const floatAmplitude = 0.15 + (i % 5) * 0.05
      const floatPhase = i * 0.15
      cityPos[idx + 1] +=
        Math.sin(time * floatSpeed + floatPhase) * floatAmplitude
    }
    cityParticles.geometry.attributes.position.needsUpdate = true

    // 地面粒子扩散
    const groundPos = groundParticles.geometry.attributes.position
      .array as Float32Array
    for (let i = 0; i < groundScatterStartPositions.length; i++) {
      const start = groundScatterStartPositions[i]
      const target = groundScatterTargets[i]
      const idx = i * 3

      groundPos[idx] = start.x + (target.x - start.x) * progress
      groundPos[idx + 1] = start.y + (target.y - start.y) * progress
      groundPos[idx + 2] = start.z + (target.z - start.z) * progress
    }
    groundParticles.geometry.attributes.position.needsUpdate = true

    return // 扩散时不执行其他动画
  }

  // 汇聚动画
  if (isCityConverging) {
    const elapsed = now - cityConvergeStartTime
    let progress = Math.min(elapsed / cityConvergeDuration, 1)
    // 缓出效果
    progress = 1 - Math.pow(1 - progress, 3)

    // 城市粒子汇聚 - 使用保存的初始位置进行插值
    const cityPos = cityParticles.geometry.attributes.position
      .array as Float32Array
    for (let i = 0; i < cityTargets.length; i++) {
      const target = cityTargets[i]
      const start = cityConvergeStartPositions[i]
      const idx = i * 3

      if (start) {
        // 从初始位置到目标位置的插值
        cityPos[idx] = start.x + (target.x - start.x) * progress
        cityPos[idx + 1] = start.y + (target.y - start.y) * progress
        cityPos[idx + 2] = start.z + (target.z - start.z) * progress
      }
    }
    cityParticles.geometry.attributes.position.needsUpdate = true

    // 地面粒子汇聚 + 波浪效果 - 使用保存的初始位置进行插值
    const groundPos = groundParticles.geometry.attributes.position
      .array as Float32Array
    for (let i = 0; i < groundTargets.length; i++) {
      const target = groundTargets[i]
      const start = groundConvergeStartPositions[i]
      const idx = i * 3

      // 目标Y加上波浪效果
      const waveY =
        Math.sin(target.x * 0.06 + time * 1.0) * 1.5 +
        Math.cos(target.z * 0.06 + time * 0.8) * 1

      if (start) {
        // 从初始位置到目标位置的插值
        groundPos[idx] = start.x + (target.x - start.x) * progress
        groundPos[idx + 1] = start.y + (target.y + waveY - start.y) * progress
        groundPos[idx + 2] = start.z + (target.z - start.z) * progress
      }
    }
    groundParticles.geometry.attributes.position.needsUpdate = true

    if (progress >= 1) {
      isCityConverging = false
    }
  } else {
    // 地面波浪动画
    const groundPos = groundParticles.geometry.attributes.position
      .array as Float32Array
    for (let i = 0; i < groundTargets.length; i++) {
      const target = groundTargets[i]
      const idx = i * 3
      groundPos[idx + 1] =
        target.y +
        Math.sin(target.x * 0.06 + time * 1.0) * 1.5 +
        Math.cos(target.z * 0.06 + time * 0.8) * 1
    }
    groundParticles.geometry.attributes.position.needsUpdate = true
  }

  // 背景粒子浮动效果（每个粒子独立浮动）
  if (bgParticles && cityBgTargetPositions.length > 0) {
    const bgPos = bgParticles.geometry.attributes.position.array as Float32Array
    const bgCount = Math.min(bgPos.length / 3, cityBgTargetPositions.length)

    for (let i = 0; i < bgCount; i++) {
      const target = cityBgTargetPositions[i]
      if (!target) continue

      const px = bgPos[i * 3]
      const py = bgPos[i * 3 + 1]

      // 每个粒子独立的浮动参数
      const floatSpeed = 0.5 + (i % 10) * 0.08
      const floatAmplitude = 0.2 + (i % 5) * 0.1
      const floatPhase = i * 0.2

      // 目标Y位置包含独立的浮动偏移
      const floatOffsetY =
        Math.sin(time * floatSpeed + floatPhase) * floatAmplitude
      const targetY = target.y + floatOffsetY

      const dx = target.x - px
      const dy = targetY - py

      // 平滑移动到目标位置（包含浮动）
      const speed = 0.08
      bgPos[i * 3] += dx * speed
      bgPos[i * 3 + 1] += dy * speed
    }
    bgParticles.geometry.attributes.position.needsUpdate = true
  }
}

// 切换到AI球体
function switchToAi() {
  currentShape = 'ai'

  // 保持白色背景
  renderer.setClearColor(0xffffff, 1)

  // 每次切换都重新生成AI球体数据，确保状态干净
  aiSphereTargets = []
  aiSphereColors = []
  aiTextTargets = []
  aiTextColors = []
  generateAiSphereData()

  // 移除旧粒子
  if (particles) scene.remove(particles)
  if (moleculeGroup) {
    scene.remove(moleculeGroup)
    moleculeGroup = null
  }
  // 移除旧背景粒子
  if (bgParticles) {
    scene.remove(bgParticles)
    // 只有当 bgParticles 不是预渲染的城市背景粒子时才 dispose
    if (bgParticles !== preCityBgParticles) {
      if (bgParticles.geometry) bgParticles.geometry.dispose()
      if (bgParticles.material)
        (bgParticles.material as THREE.Material).dispose()
    }
    bgParticles = null
  }
  // 清理旧的AI粒子（如果有）
  if (aiGroup) {
    scene.remove(aiGroup)
    aiGroup = null
  }
  if (aiTextGroup) {
    scene.remove(aiTextGroup)
    aiTextGroup = null
  }
  if (aiSphereParticles) {
    aiSphereParticles.geometry.dispose()
    ;(aiSphereParticles.material as THREE.Material).dispose()
    aiSphereParticles = null
  }
  if (aiTextParticles) {
    aiTextParticles.geometry.dispose()
    ;(aiTextParticles.material as THREE.Material).dispose()
    aiTextParticles = null
  }

  // 隐藏 logo 图片
  hideLogoImage()

  // 重置AI状态
  aiRotationX = 0
  aiRotationY = 0
  aiTextRotationX = 0
  aiTextRotationY = 0
  aiScale = 1.0
  aiFormationComplete = false
  aiAutoRotationActive = false
  aiRotationSpeed = 0 // 重置旋转速度，让旋转平滑启动
  aiConvergeStartTime = 0 // 重置汇聚开始时间

  // 清空并重新生成初始位置数组
  aiSphereConvergeStartPositions = []
  aiTextConvergeStartPositions = []

  // 创建AI球体粒子
  const spherePositions: number[] = []
  const sphereColors: number[] = []
  const sphereSizes: number[] = []

  for (let i = 0; i < aiSphereTargets.length; i++) {
    const target = aiSphereTargets[i]
    // 初始位置：从目标位置的延长线方向开始，直线飞入
    // 计算从中心到目标的方向
    const dirX = target.x
    const dirY = target.y
    const dirZ = target.z
    const dist = Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ)

    let x, y, z
    if (dist > 0.1) {
      // 从目标位置向外延伸，让粒子从外向内直线飞入
      const flyDist = 15 + Math.random() * 20
      const normalizedX = dirX / dist
      const normalizedY = dirY / dist
      const normalizedZ = dirZ / dist
      x = target.x + normalizedX * flyDist + (Math.random() - 0.5) * 3
      y = target.y + normalizedY * flyDist + (Math.random() - 0.5) * 3
      z = target.z + normalizedZ * flyDist + (Math.random() - 0.5) * 3
    } else {
      // 目标在中心附近，随机方向飞入
      const angle = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      const flyDist = 15 + Math.random() * 20
      x = Math.sin(phi) * Math.cos(angle) * flyDist
      y = Math.sin(phi) * Math.sin(angle) * flyDist
      z = Math.cos(phi) * flyDist
    }

    spherePositions.push(x, y, z)
    aiSphereConvergeStartPositions.push({ x, y, z })

    const color = aiSphereColors[i]
    sphereColors.push(color.r, color.g, color.b)

    // 随机大小
    const sizeRand = Math.random()
    if (sizeRand < 0.6) {
      sphereSizes.push(0.08 + Math.random() * 0.08)
    } else if (sizeRand < 0.9) {
      sphereSizes.push(0.12 + Math.random() * 0.1)
    } else {
      sphereSizes.push(0.2 + Math.random() * 0.15)
    }
  }

  const sphereGeometry = new THREE.BufferGeometry()
  sphereGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(spherePositions, 3),
  )
  sphereGeometry.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(sphereColors, 3),
  )
  sphereGeometry.setAttribute(
    'size',
    new THREE.Float32BufferAttribute(sphereSizes, 1),
  )

  const sphereMaterial = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
      holeRadius: { value: isMobile ? 4.5 : 4.5 }, // 正面洞的半径
      holeCenterY: { value: isMobile ? -2.0 : 0.0 }, // 洞中心的Y偏移（相对于屏幕中心）
    },
    vertexShader: `
      uniform float sizeScale;
      uniform float holeRadius;
      uniform float holeCenterY;
      attribute vec3 color;
      attribute float size;
      varying vec3 vColor;
      varying float vDepth;
      varying float vHide;

      void main() {
        vColor = color;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vDepth = -mvPosition.z;
        
        // 洞的中心在屏幕空间中的Y位置（可以调整以匹配球体的视觉中心）
        float holeCenterYPos = holeCenterY;
        
        // 正面洞
        float frontXyDist = length(vec2(mvPosition.x, mvPosition.y - holeCenterYPos));
        float frontness = smoothstep(-12.0, -6.0, mvPosition.z); // 越靠近相机，值越大
        float frontHide = frontness * (1.0 - smoothstep(holeRadius * 0.8, holeRadius, frontXyDist));
        
        // 背面洞
        float backXyDist = length(vec2(mvPosition.x, mvPosition.y - holeCenterYPos));
        float backness = smoothstep(-25.0, -18.0, -mvPosition.z); // 越远离相机，值越大
        float backHide = backness * (1.0 - smoothstep(holeRadius * 0.8, holeRadius, backXyDist));
        
        // 取两者最大值
        vHide = max(frontHide, backHide);
        
        gl_PointSize = size * 300.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying float vDepth;
      varying float vHide;

      void main() {
        // 如果标记为隐藏，丢弃这个片元
        if (vHide > 0.8) discard;
        
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        // 实心圆点效果，边缘柔和
        float alpha = 1.0 - smoothstep(0.3, 0.5, dist);
        alpha = pow(alpha, 0.8);
        
        // 根据隐藏程度降低透明度，实现平滑过渡
        alpha *= (1.0 - vHide);

        gl_FragColor = vec4(vColor, alpha * 0.85);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  aiSphereParticles = new THREE.Points(sphereGeometry, sphereMaterial)

  // 创建AI文字粒子
  const textPositions: number[] = []
  const textColors: number[] = []
  const textSizes: number[] = []

  for (let i = 0; i < aiTextTargets.length; i++) {
    const target = aiTextTargets[i]
    // 初始位置：从随机方向飞入，直线汇聚到文字位置
    const angle = Math.random() * Math.PI * 2
    const flyDist = 8 + Math.random() * 12
    const x = target.x + Math.cos(angle) * flyDist + (Math.random() - 0.5) * 2
    const y = target.y + Math.sin(angle) * flyDist + (Math.random() - 0.5) * 2
    const z = target.z + (Math.random() - 0.5) * 8

    textPositions.push(x, y, z)
    aiTextConvergeStartPositions.push({ x, y, z })

    const color = aiTextColors[i]
    textColors.push(color.r, color.g, color.b)

    // 文字粒子稍大一些
    textSizes.push(0.15 + Math.random() * 0.1)
  }

  const textGeometry = new THREE.BufferGeometry()
  textGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(textPositions, 3),
  )
  textGeometry.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(textColors, 3),
  )
  textGeometry.setAttribute(
    'size',
    new THREE.Float32BufferAttribute(textSizes, 1),
  )

  const textMaterial = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
      time: { value: 0 },
    },
    vertexShader: `
      uniform float sizeScale;
      uniform float time;
      attribute vec3 color;
      attribute float size;
      varying vec3 vColor;

      void main() {
        vColor = color;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * 350.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        // 实心圆点，边缘柔和
        float alpha = 1.0 - smoothstep(0.25, 0.5, dist);
        alpha = pow(alpha, 0.7);

        gl_FragColor = vec4(vColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  aiTextParticles = new THREE.Points(textGeometry, textMaterial)

  // 创建AI Group用于旋转
  aiGroup = new THREE.Group()
  aiGroup.add(aiSphereParticles)
  scene.add(aiGroup)

  // AI文字放到单独的Group中，用于浮动和拖动旋转
  aiTextGroup = new THREE.Group()
  aiTextGroup.add(aiTextParticles)
  scene.add(aiTextGroup)

  // 重置AI文字旋转角度
  aiTextRotationX = 0
  aiTextRotationY = 0

  // 创建背景粒子
  createAiBackgroundParticles()

  // 调整相机
  camera.position.z = isMobile ? 18 : 15
  camera.position.x = 0
  camera.position.y = isMobile ? -5 : 0
  camera.lookAt(0, isMobile ? 3 : 0, 0)

  // 开始汇聚动画
  isAiConverging = true
  aiConvergeStartTime = Date.now()

  // 启动动画循环
  animate()
}

// 创建AI模式的背景粒子
function createAiBackgroundParticles() {
  // 使用与圆圈/分子模式一致的背景粒子颜色
  const bgColors = [
    new THREE.Color(0x639ee9),
    new THREE.Color(0x5fcbf2),
    new THREE.Color(0x6cc6ac),
    new THREE.Color(0xa1d5c1),
  ]

  // 使用与圆圈模式一致的背景粒子数量
  const bgParticleCount = config.circleBgParticleCount
  const bgPositions: number[] = []
  const bgColorsArr: number[] = []
  const bgSizes: number[] = []
  bgTargetPositions = []

  const aspect = window.innerWidth / window.innerHeight
  const vFov = (camera.fov * Math.PI) / 180
  const height = 2 * Math.tan(vFov / 2) * 20
  const width = height * aspect

  // 与圆圈模式一致的分布范围
  const bgSpreadMultiplierX = isMobile ? 5.0 : 3.0
  const bgSpreadMultiplierY = isMobile ? 2.5 : 1.8

  for (let i = 0; i < bgParticleCount; i++) {
    // 目标位置：最终要到达的位置，横向占满全屏
    const targetX = (Math.random() - 0.5) * width * bgSpreadMultiplierX
    const targetY = (Math.random() - 0.5) * height * bgSpreadMultiplierY
    const targetZ = -3 - Math.random() * 7
    bgTargetPositions.push({ x: targetX, y: targetY, z: targetZ })

    // 初始位置：从屏幕外随机位置开始飞入
    const flyInAngle = Math.random() * Math.PI * 2
    const flyInDistance = 30 + Math.random() * 40
    bgPositions.push(
      Math.cos(flyInAngle) * flyInDistance + (Math.random() - 0.5) * 10,
      Math.sin(flyInAngle) * flyInDistance + (Math.random() - 0.5) * 10,
      -10 - Math.random() * 10,
    )

    const bgColor = bgColors[Math.floor(Math.random() * bgColors.length)]
    bgColorsArr.push(bgColor.r, bgColor.g, bgColor.b)

    // 随机大小：与圆圈模式一致，大小差距更明显
    const sizeRand = Math.random()
    if (sizeRand < 0.5) {
      bgSizes.push(0.04 + Math.random() * 0.08) // 很小的粒子
    } else if (sizeRand < 0.75) {
      bgSizes.push(0.15 + Math.random() * 0.15) // 小粒子
    } else if (sizeRand < 0.9) {
      bgSizes.push(0.35 + Math.random() * 0.25) // 中粒子
    } else if (sizeRand < 0.97) {
      bgSizes.push(0.5 + Math.random() * 0.3) // 大粒子
    } else {
      bgSizes.push(0.8 + Math.random() * 0.4) // 特大粒子
    }
  }

  const bgGeometry = new THREE.BufferGeometry()
  bgGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(bgPositions, 3),
  )
  bgGeometry.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(bgColorsArr, 3),
  )
  bgGeometry.setAttribute('size', new THREE.Float32BufferAttribute(bgSizes, 1))

  // 与圆圈模式一致的shader
  const bgMaterial = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
      time: { value: 0 },
      clickPos: { value: new THREE.Vector3() },
      clickProgress: { value: 0 },
      clickActive: { value: 0 },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute vec3 color;
      attribute float size;
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform vec3 clickPos;
      uniform float clickProgress;
      uniform float clickActive;

      void main() {
        vColor = color;
        vPosition = position;

        vDistToClick = distance(position, clickPos);

        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * 300.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform float clickProgress;
      uniform float clickActive;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        // 更锐利的边缘，提高清晰度
        float alpha = 1.0 - smoothstep(0.3, 0.5, dist);

        vec3 finalColor = vColor;

        if (clickActive > 0.5) {
          float effectRadius = 5.0;
          float influence = 1.0 - clamp(vDistToClick / effectRadius, 0.0, 1.0);

          if (clickProgress < 1.0) {
            float brightness = 1.0 + influence * clickProgress * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * clickProgress * 0.6) * brightness;
          } else {
            float fadeProgress = clickProgress - 1.0;
            float brightness = 1.0 + influence * (1.0 - fadeProgress) * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * (1.0 - fadeProgress) * 0.6) * brightness;
          }
        }

        gl_FragColor = vec4(finalColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  bgParticles = new THREE.Points(bgGeometry, bgMaterial)
  scene.add(bgParticles)
}

// AI球体动画
function animateAi() {
  const time = Date.now() * 0.001
  const now = Date.now()

  if (!aiSphereParticles || !aiTextParticles || !aiGroup) return

  // 汇聚开始后立即启动自动旋转（不等待）
  if (aiConvergeStartTime > 0 && !aiAutoRotationActive && !isDragging) {
    aiAutoRotationActive = true
  }

  // 球体自动旋转 - 平滑启动
  if (aiAutoRotationActive && !isDragging) {
    // 旋转速度平滑增加到目标速度
    if (aiRotationSpeed < aiTargetRotationSpeed) {
      aiRotationSpeed += 0.00015 // 更快加速
      if (aiRotationSpeed > aiTargetRotationSpeed) {
        aiRotationSpeed = aiTargetRotationSpeed
      }
    }
    aiRotationY += aiRotationSpeed
    if (aiRotationY > Math.PI * 2) {
      aiRotationY -= Math.PI * 2
    }
  }

  // 应用球体旋转和缩放
  aiGroup.rotation.x = aiRotationX
  aiGroup.rotation.y = aiRotationY
  aiGroup.scale.setScalar(aiScale)

  // AI文字Group的浮动效果和拖动旋转（汇聚时也生效）
  if (aiTextGroup) {
    // 上下浮动效果 - 增大幅度让效果更明显
    const floatY = Math.sin(time * 0.8) * 0.25
    aiTextGroup.position.y = floatY

    // 应用拖动旋转（只在拖动时改变）
    aiTextGroup.rotation.x = aiTextRotationX
    aiTextGroup.rotation.y = aiTextRotationY
  }

  // 汇聚动画
  if (isAiConverging) {
    const elapsed = now - aiConvergeStartTime
    let progress = Math.min(elapsed / aiConvergeDuration, 1)
    progress = 1 - Math.pow(1 - progress, 3)

    // 球体粒子汇聚
    const spherePos = aiSphereParticles.geometry.attributes.position
      .array as Float32Array
    for (let i = 0; i < aiSphereTargets.length; i++) {
      const target = aiSphereTargets[i]
      const start = aiSphereConvergeStartPositions[i]
      const idx = i * 3

      if (start) {
        spherePos[idx] = start.x + (target.x - start.x) * progress
        spherePos[idx + 1] = start.y + (target.y - start.y) * progress
        spherePos[idx + 2] = start.z + (target.z - start.z) * progress
      }
    }
    aiSphereParticles.geometry.attributes.position.needsUpdate = true

    // 文字粒子汇聚（稍微延迟）
    const textProgress = Math.max(0, (progress - 0.2) / 0.8)
    const textPos = aiTextParticles.geometry.attributes.position
      .array as Float32Array
    for (let i = 0; i < aiTextTargets.length; i++) {
      const target = aiTextTargets[i]
      const start = aiTextConvergeStartPositions[i]
      const idx = i * 3

      if (start) {
        textPos[idx] = start.x + (target.x - start.x) * textProgress
        textPos[idx + 1] = start.y + (target.y - start.y) * textProgress
        textPos[idx + 2] = start.z + (target.z - start.z) * textProgress
      }
    }
    aiTextParticles.geometry.attributes.position.needsUpdate = true

    if (progress >= 1) {
      isAiConverging = false
      aiFormationComplete = true
    }
  } else {
    // 球体粒子浮动效果
    const spherePos = aiSphereParticles.geometry.attributes.position
      .array as Float32Array
    for (let i = 0; i < aiSphereTargets.length; i++) {
      const target = aiSphereTargets[i]
      const idx = i * 3

      const floatSpeed = 0.5 + (i % 10) * 0.05
      const floatAmplitude = 0.05 + (i % 5) * 0.02
      const floatPhase = i * 0.1

      spherePos[idx] =
        target.x + Math.sin(time * floatSpeed + floatPhase) * floatAmplitude
      spherePos[idx + 1] =
        target.y +
        Math.cos(time * floatSpeed * 0.8 + floatPhase) * floatAmplitude
      spherePos[idx + 2] =
        target.z +
        Math.sin(time * floatSpeed * 0.6 + floatPhase) * floatAmplitude * 0.5
    }
    aiSphereParticles.geometry.attributes.position.needsUpdate = true

    // 文字粒子微小浮动效果（相对于文字Group）
    const textPos = aiTextParticles.geometry.attributes.position
      .array as Float32Array
    for (let i = 0; i < aiTextTargets.length; i++) {
      const target = aiTextTargets[i]
      const idx = i * 3

      const floatSpeed = 0.3 + (i % 8) * 0.03
      const floatAmplitude = 0.02 + (i % 4) * 0.01
      const floatPhase = i * 0.15

      textPos[idx] =
        target.x + Math.sin(time * floatSpeed + floatPhase) * floatAmplitude
      textPos[idx + 1] =
        target.y + Math.cos(time * floatSpeed + floatPhase) * floatAmplitude
      textPos[idx + 2] = target.z
    }
    aiTextParticles.geometry.attributes.position.needsUpdate = true

    // 更新文字材质的时间uniform
    const textMaterial = aiTextParticles.material as THREE.ShaderMaterial
    if (textMaterial.uniforms.time) {
      textMaterial.uniforms.time.value = time
    }
  }

  // 背景粒子汇聚和浮动（每个粒子独立浮动）
  if (bgParticles && bgTargetPositions.length > 0) {
    const bgPos = bgParticles.geometry.attributes.position.array as Float32Array
    const bgCount = bgPos.length / 3

    for (let i = 0; i < bgCount; i++) {
      const target = bgTargetPositions[i]
      if (!target) continue

      const px = bgPos[i * 3]
      const py = bgPos[i * 3 + 1]
      const pz = bgPos[i * 3 + 2]

      // 每个粒子独立的浮动参数
      const floatSpeed = 0.5 + (i % 10) * 0.08
      const floatAmplitude = 0.2 + (i % 5) * 0.1
      const floatPhase = i * 0.2

      // 目标Y位置包含独立的浮动偏移
      const floatOffsetY =
        Math.sin(time * floatSpeed + floatPhase) * floatAmplitude
      const targetY = target.y + floatOffsetY

      const dx = target.x - px
      const dy = targetY - py
      const dz = target.z - pz

      // 平滑移动到目标位置（包含浮动）
      const speed = 0.08
      bgPos[i * 3] += dx * speed
      bgPos[i * 3 + 1] += dy * speed
      bgPos[i * 3 + 2] += dz * speed
    }
    bgParticles.geometry.attributes.position.needsUpdate = true
  }
}

function animate() {
  animationId = requestAnimationFrame(animate)

  // 处理 switchTo 扩散动画
  if (isSwitchToScattering) {
    animateSwitchToScatter()
    renderer.render(scene, camera)
    return
  }

  if (currentShape === 'circle') {
    animateCircle()
  } else if (currentShape === 'city') {
    animateCity()
  } else if (currentShape === 'ai') {
    animateAi()
  } else {
    animateMolecule()
  }

  renderer.render(scene, camera)
}

// switchTo 扩散动画
function animateSwitchToScatter() {
  const now = Date.now()
  const elapsed = now - switchToScatterStartTime
  let progress = Math.min(elapsed / switchToScatterDuration, 1)
  progress = 1 - Math.pow(1 - progress, 2.5) // 缓出效果

  // 根据当前形状执行对应的扩散动画
  if (currentShape === 'circle' && particles) {
    // 圆圈粒子扩散
    const positions = particles.geometry.attributes.position
      .array as Float32Array
    const count = Math.min(
      switchToScatterStartPositions.length,
      positions.length / 3,
    )

    for (let i = 0; i < count; i++) {
      const start = switchToScatterStartPositions[i]
      const target = switchToScatterTargets[i]
      if (start && target) {
        positions[i * 3] = start.x + (target.x - start.x) * progress
        positions[i * 3 + 1] = start.y + (target.y - start.y) * progress
        positions[i * 3 + 2] = start.z + (target.z - start.z) * progress
      }
    }
    particles.geometry.attributes.position.needsUpdate = true
  } else if (currentShape === 'molecule' && particles) {
    // 分子粒子扩散
    const positions = particles.geometry.attributes.position
      .array as Float32Array
    const count = Math.min(
      switchToScatterStartPositions.length,
      positions.length / 3,
    )

    for (let i = 0; i < count; i++) {
      const start = switchToScatterStartPositions[i]
      const target = switchToScatterTargets[i]
      if (start && target) {
        positions[i * 3] = start.x + (target.x - start.x) * progress
        positions[i * 3 + 1] = start.y + (target.y - start.y) * progress
        positions[i * 3 + 2] = start.z + (target.z - start.z) * progress
      }
    }
    particles.geometry.attributes.position.needsUpdate = true
  } else if (currentShape === 'city' && cityParticles && groundParticles) {
    // 城市粒子扩散
    const cityPos = cityParticles.geometry.attributes.position
      .array as Float32Array
    for (let i = 0; i < cityScatterStartPositions.length; i++) {
      const start = cityScatterStartPositions[i]
      const target = cityScatterTargets[i]
      if (start && target) {
        cityPos[i * 3] = start.x + (target.x - start.x) * progress
        cityPos[i * 3 + 1] = start.y + (target.y - start.y) * progress
        cityPos[i * 3 + 2] = start.z + (target.z - start.z) * progress
      }
    }
    cityParticles.geometry.attributes.position.needsUpdate = true

    // 地面粒子扩散
    const groundPos = groundParticles.geometry.attributes.position
      .array as Float32Array
    for (let i = 0; i < groundScatterStartPositions.length; i++) {
      const start = groundScatterStartPositions[i]
      const target = groundScatterTargets[i]
      if (start && target) {
        groundPos[i * 3] = start.x + (target.x - start.x) * progress
        groundPos[i * 3 + 1] = start.y + (target.y - start.y) * progress
        groundPos[i * 3 + 2] = start.z + (target.z - start.z) * progress
      }
    }
    groundParticles.geometry.attributes.position.needsUpdate = true
  } else if (currentShape === 'ai' && aiSphereParticles) {
    // AI球体粒子扩散
    const spherePos = aiSphereParticles.geometry.attributes.position
      .array as Float32Array
    const count = Math.min(
      switchToScatterStartPositions.length,
      spherePos.length / 3,
    )

    for (let i = 0; i < count; i++) {
      const start = switchToScatterStartPositions[i]
      const target = switchToScatterTargets[i]
      if (start && target) {
        spherePos[i * 3] = start.x + (target.x - start.x) * progress
        spherePos[i * 3 + 1] = start.y + (target.y - start.y) * progress
        spherePos[i * 3 + 2] = start.z + (target.z - start.z) * progress
      }
    }
    aiSphereParticles.geometry.attributes.position.needsUpdate = true

    // AI 文字粒子扩散
    if (aiTextParticles && switchToAiTextScatterStartPositions.length > 0) {
      const textPos = aiTextParticles.geometry.attributes.position
        .array as Float32Array
      const textCount = Math.min(
        switchToAiTextScatterStartPositions.length,
        textPos.length / 3,
      )

      for (let i = 0; i < textCount; i++) {
        const start = switchToAiTextScatterStartPositions[i]
        const target = switchToAiTextScatterTargets[i]
        if (start && target) {
          textPos[i * 3] = start.x + (target.x - start.x) * progress
          textPos[i * 3 + 1] = start.y + (target.y - start.y) * progress
          textPos[i * 3 + 2] = start.z + (target.z - start.z) * progress
        }
      }
      aiTextParticles.geometry.attributes.position.needsUpdate = true
    }
  }

  // 背景粒子扩散（圆圈、分子、AI模式共用）
  if (bgParticles && switchToBgScatterStartPositions.length > 0) {
    const bgPos = bgParticles.geometry.attributes.position.array as Float32Array
    const bgCount = Math.min(
      switchToBgScatterStartPositions.length,
      bgPos.length / 3,
    )

    for (let i = 0; i < bgCount; i++) {
      const start = switchToBgScatterStartPositions[i]
      const target = switchToBgScatterTargets[i]
      if (start && target) {
        bgPos[i * 3] = start.x + (target.x - start.x) * progress
        bgPos[i * 3 + 1] = start.y + (target.y - start.y) * progress
        bgPos[i * 3 + 2] = start.z + (target.z - start.z) * progress
      }
    }
    bgParticles.geometry.attributes.position.needsUpdate = true
  }

  // 扩散完成，切换到目标形状
  if (progress >= 1) {
    isSwitchToScattering = false
    isCityScattering = false
    finishSwitchTo(switchToTargetShape)
  }
}

function animateCircle() {
  const now = Date.now()
  const elapsed = circleInitTime > 0 ? now - circleInitTime : 0

  // 总动画时间：直接飞入汇聚
  const totalDuration = circleFlyInDuration // 800ms 直接飞入汇聚
  const totalProgress = Math.min(elapsed / totalDuration, 1)

  // 检查是否完成
  if (totalProgress >= 1 && !circleComplete) {
    circleComplete = true
    // 圆圈汇聚完成后，开始显示 logo 图片
    startLogoAnimation()
  }

  const time = now * 0.001
  circleFloatOffset = Math.sin(time * 0.8) * 0.3

  if (isTransitioning) {
    if (transitionState === 'scatter') {
      // 加快扩散速度
      transitionProgress += 0.012 * (1.15 - transitionProgress)
      if (transitionProgress >= 0.98) {
        transitionProgress = 1
        transitionState = 'pause'
        transitionProgress = 0
      }
    } else if (transitionState === 'pause') {
      // 立即切换，无定格
      switchToMolecule()
      isTransitioning = false
    }
  }

  // 处理切换到城市的扩散动画
  if (isTransitioningToCity) {
    if (transitionToCityState === 'scatter') {
      transitionToCityProgress += 0.012 * (1.15 - transitionToCityProgress)
      if (transitionToCityProgress >= 0.98) {
        transitionToCityProgress = 1
        transitionToCityState = 'pause'
      }
    }
  }

  const positions = particles.geometry.attributes.position.array as Float32Array
  const sizes = particles.geometry.attributes.size.array as Float32Array

  for (let i = 0; i < particleCount; i++) {
    const start = circleStartPositions[i]
    const scatter = circleScatterPositions[i]
    const target = circleTargetPositions[i]

    if (!start || !scatter || !target) continue

    let targetX: number, targetY: number, targetZ: number

    // 切换到城市的扩散动画
    if (
      isTransitioningToCity &&
      (transitionToCityState === 'scatter' || transitionToCityState === 'pause')
    ) {
      const scatterTarget = circleToCityScatterTargets[i]
      const startPos = circleToCityStartPositions[i] || {
        x: positions[i * 3],
        y: positions[i * 3 + 1],
        z: positions[i * 3 + 2],
      }
      const rawProg =
        transitionToCityState === 'scatter' ? transitionToCityProgress : 1.0

      const prog = 1 - Math.pow(1 - rawProg, 2.5)

      if (scatterTarget) {
        targetX = startPos.x + (scatterTarget.x - startPos.x) * prog
        targetY = startPos.y + (scatterTarget.y - startPos.y) * prog
        targetZ = startPos.z + (scatterTarget.z - startPos.z) * prog
      } else {
        targetX = startPos.x
        targetY = startPos.y
        targetZ = startPos.z
      }

      const floatSpeed = 0.8 + (i % 10) * 0.08
      const floatAmplitude = 0.15 + (i % 5) * 0.05
      const floatPhase = i * 0.15
      targetY += Math.sin(time * floatSpeed + floatPhase) * floatAmplitude
      targetX +=
        Math.cos(time * floatSpeed * 0.7 + floatPhase) * floatAmplitude * 0.3
    } else if (
      isTransitioning &&
      (transitionState === 'scatter' || transitionState === 'pause')
    ) {
      // 转场扩散动画（切换到分子）
      const scatterTarget = scatterTargets[i]
      const startPos = scatterStartPositions[i] || {
        x: positions[i * 3],
        y: positions[i * 3 + 1],
        z: positions[i * 3 + 2],
      }
      const rawProg = transitionState === 'scatter' ? transitionProgress : 1.0

      // 使用缓出缓动函数，让扩散更柔和
      const prog = 1 - Math.pow(1 - rawProg, 2.5)

      targetX = startPos.x + (scatterTarget.x - startPos.x) * prog
      targetY = startPos.y + (scatterTarget.y - startPos.y) * prog
      targetZ = startPos.z + (scatterTarget.z - startPos.z) * prog

      // 扩散和暂停时都添加浮动效果，让粒子更灵动
      const floatSpeed = 0.8 + (i % 10) * 0.08
      const floatAmplitude = 0.15 + (i % 5) * 0.05
      const floatPhase = i * 0.15
      targetY += Math.sin(time * floatSpeed + floatPhase) * floatAmplitude
      targetX +=
        Math.cos(time * floatSpeed * 0.7 + floatPhase) * floatAmplitude * 0.3
    } else {
      // 直接从起点飞入汇聚成圆圈
      // 使用强缓出效果，让结束时速度更慢，抵消透视带来的加速感
      const easedProgress = Math.sqrt(totalProgress)

      // 直接从起点飞到目标位置
      targetX = start.x + (target.x - start.x) * easedProgress
      targetY = start.y + (target.y - start.y) * easedProgress
      targetZ = start.z + (target.z - start.z) * easedProgress

      // 浮动效果只在汇聚完成后才生效，避免汇聚过程中的螺旋感
      if (totalProgress >= 1) {
        const floatSpeed = 0.6 + (i % 10) * 0.08
        const floatAmplitude = 0.2 + (i % 5) * 0.06
        const floatPhase = i * 0.15
        targetY += Math.sin(time * floatSpeed + floatPhase) * floatAmplitude
        targetX +=
          Math.cos(time * floatSpeed * 0.7 + floatPhase) * floatAmplitude * 0.4
      }
    }

    positions[i * 3] = targetX
    positions[i * 3 + 1] = targetY
    positions[i * 3 + 2] = targetZ
  }

  // 鼠标悬停效果 - 在屏幕空间中计算距离，避免相机倾斜带来的偏差
  // 将鼠标位置转换为屏幕坐标 (-1 到 1)
  const mouseScreenX = mouseX
  const mouseScreenY = mouseY

  // hover 半径（屏幕空间，-1 到 1 的范围）
  const hoverScreenRadius = isMobile ? 0.15 : 0.12

  const tempVec = new THREE.Vector3()

  for (let i = 0; i < particleCount; i++) {
    const px = positions[i * 3]
    const py = positions[i * 3 + 1]
    const pz = positions[i * 3 + 2]
    const originalSize = originalSizes[i]

    // 将粒子位置投影到屏幕空间
    tempVec.set(px, py, pz)
    tempVec.project(camera)

    // 计算屏幕空间中的距离
    const screenDx = tempVec.x - mouseScreenX
    const screenDy = tempVec.y - mouseScreenY
    const screenDist = Math.sqrt(screenDx * screenDx + screenDy * screenDy)

    // 确保粒子在相机前方（z 在 -1 到 1 之间表示在视锥体内）
    const isVisible = tempVec.z >= -1 && tempVec.z <= 1

    if (isVisible && screenDist < hoverScreenRadius) {
      const hoverFactor = 1.0 - screenDist / hoverScreenRadius
      // 放大倍数
      const sizeMultiplier = 1.0 + Math.pow(hoverFactor, 0.7) * 1.2
      sizes[i] = originalSize * sizeMultiplier
    } else {
      sizes[i] = originalSize
    }
  }

  particles.geometry.attributes.position.needsUpdate = true
  particles.geometry.attributes.size.needsUpdate = true

  // 背景粒子汇聚动画和浮动效果（每个粒子独立浮动）
  if (bgParticles && bgTargetPositions.length > 0) {
    const bgPositions = bgParticles.geometry.attributes.position
      .array as Float32Array
    const bgCount = bgPositions.length / 3

    for (let i = 0; i < bgCount; i++) {
      const target = bgTargetPositions[i]
      if (!target) continue

      const px = bgPositions[i * 3]
      const py = bgPositions[i * 3 + 1]
      const pz = bgPositions[i * 3 + 2]

      // 每个粒子独立的浮动参数
      const floatSpeed = 0.5 + (i % 10) * 0.08
      const floatAmplitude = 0.2 + (i % 5) * 0.1
      const floatPhase = i * 0.2

      // 目标Y位置包含独立的浮动偏移
      const floatOffsetY =
        Math.sin(time * floatSpeed + floatPhase) * floatAmplitude
      const targetY = target.y + floatOffsetY

      const dx = target.x - px
      const dy = targetY - py
      const dz = target.z - pz

      // 平滑移动到目标位置（包含浮动）
      const speed = 0.08
      bgPositions[i * 3] += dx * speed
      bgPositions[i * 3 + 1] += dy * speed
      bgPositions[i * 3 + 2] += dz * speed
    }

    bgParticles.geometry.attributes.position.needsUpdate = true
  }
}

function createLogoParticles() {
  const positions: number[] = []
  const colors: number[] = []
  const sizes: number[] = []

  for (let i = 0; i < logoTargets.length; i++) {
    positions.push(0, 0, 1.0)

    const color = logoColors[i]
    colors.push(color.r, color.g, color.b)

    const r = color.r * 255
    const g = color.g * 255
    const b = color.b * 255

    const isWhite = r > 200 && g > 200 && b > 200
    const isGray =
      Math.abs(r - g) < 30 && Math.abs(g - b) < 30 && r > 50 && r < 200
    const isCyanOrBlue = (g > 100 || b > 100) && r < 200
    const isBlack = r < 50 && g < 50 && b < 50

    if (isBlack) {
      sizes.push(0.35 + Math.random() * 0.25)
    } else if (isWhite) {
      sizes.push(0.15 + Math.random() * 0.1)
    } else if (isGray) {
      sizes.push(0.2 + Math.random() * 0.15)
    } else if (isCyanOrBlue) {
      sizes.push(0.28 + Math.random() * 0.18)
    } else {
      sizes.push(0.2 + Math.random() * 0.15)
    }
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3),
  )
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))
  geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1))

  const material = new THREE.ShaderMaterial({
    uniforms: { sizeScale: { value: pixelRatioScale } },
    vertexShader: `
      uniform float sizeScale;
      attribute float size;
      attribute vec3 color;
      varying vec3 vColor;

      void main() {
        vColor = color;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * 150.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        // 更锐利的边缘，提高清晰度
        float alpha = 1.0 - smoothstep(0.3, 0.5, dist);
        alpha = pow(alpha, 0.5);

        gl_FragColor = vec4(vColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  logoParticles = new THREE.Points(geometry, material)
  scene.add(logoParticles)
}

// 启动 logo 图片动画（从下往上滚动出来）
function startLogoAnimation() {
  if (showLogoImage.value) return // 已经在显示

  showLogoImage.value = true
  logoRevealPercent.value = 0
  logoOpacity.value = 0

  const startTime = Date.now()

  const animate = () => {
    const elapsed = Date.now() - startTime
    const progress = Math.min(elapsed / logoAnimationDuration, 1)

    // 使用缓出效果让动画更自然
    const easedProgress = 1 - Math.pow(1 - progress, 3)

    // 从下往上显示，0% -> 100%
    logoRevealPercent.value = easedProgress * 100
    // 透明度快速到达 1
    logoOpacity.value = Math.min(progress * 2, 1)

    if (progress < 1) {
      logoAnimationId = requestAnimationFrame(animate)
    } else {
      logoAnimationId = null
    }
  }

  logoAnimationId = requestAnimationFrame(animate)
}

// 隐藏 logo 图片
function hideLogoImage() {
  if (logoAnimationId) {
    cancelAnimationFrame(logoAnimationId)
    logoAnimationId = null
  }
  showLogoImage.value = false
  logoRevealPercent.value = 0
  logoOpacity.value = 0
}

// 旧的粒子 logo 动画函数（保留但不再使用）
function animateLogo() {
  if (!logoParticles) return
  const positions = logoParticles.geometry.attributes.position
    .array as Float32Array
  const time = Date.now() * 0.001

  // 使用缓出效果让扩展更自然
  const easedGrowth = 1 - Math.pow(1 - logoGrowthProgress, 3)

  for (let i = 0; i < logoTargets.length; i++) {
    const target = logoTargets[i]

    const distToCenter = Math.sqrt(target.x * target.x + target.y * target.y)
    const normalizedDist = logoMaxRadius > 0 ? distToCenter / logoMaxRadius : 0

    // 从中心向外扩展：距离中心越近的粒子越先出现
    if (easedGrowth > normalizedDist) {
      // 计算当前粒子的局部进度
      const localProgress = Math.min(
        (easedGrowth - normalizedDist) / 0.3, // 扩展带宽度
        1.0,
      )
      // 使用缓出效果让粒子平滑到达目标位置
      const eased = 1 - Math.pow(1 - localProgress, 4)

      const currentX = target.x * eased
      const currentY = target.y * eased

      positions[i * 3] = currentX
      positions[i * 3 + 1] = currentY
      positions[i * 3 + 2] = 0.5 // 显示在圆圈前面

      // 粒子到达目标位置后添加浮动效果
      if (localProgress >= 1.0) {
        const floatSpeed = 0.5 + (i % 10) * 0.05
        const floatAmplitude = 0.03 + (i % 5) * 0.01
        const floatPhase = i * 0.1

        positions[i * 3 + 1] +=
          Math.sin(time * floatSpeed + floatPhase) * floatAmplitude
      }
    } else {
      // 还未扩展到的粒子隐藏在中心
      positions[i * 3] = 0
      positions[i * 3 + 1] = 0
      positions[i * 3 + 2] = -200 // 隐藏在远处
    }
  }

  logoParticles.geometry.attributes.position.needsUpdate = true
}

function animateMolecule() {
  if (isTransitioning) {
    if (transitionState === 'scatter') {
      // 使用缓出效果，开始快后面慢 - 减慢速度
      transitionProgress += 0.008 * (1.2 - transitionProgress)
      if (transitionProgress >= 0.98) {
        transitionProgress = 1
        transitionState = 'pause'
        transitionProgress = 0
      }
    } else if (transitionState === 'pause') {
      transitionProgress += 0.016
      if (transitionProgress >= 0.1) {
        transitionState = 'converge'
        transitionProgress = 0
        if (currentImageIndex === 3) {
          switchToThirdImage()
        } else if (currentImageIndex === 4) {
          switchToFourthImage()
        }
        isTransitioning = false
      }
    }
  }

  const positions = particles.geometry.attributes.position.array as Float32Array
  const originalParticleCount = moleculeTargets.length
  const time = Date.now() * 0.001

  // 整体上下浮动效果
  const floatOffset = Math.sin(time * 0.8) * 0.15

  // 自动循环旋转逻辑（360度循环）
  if (autoRotationActive && !isDragging) {
    moleculeRotationY += autoRotationSpeed
    // 保持角度在合理范围内，防止数值溢出
    if (moleculeRotationY > Math.PI * 2) {
      moleculeRotationY -= Math.PI * 2
    }
  }

  // 应用鼠标拖动旋转和缩放
  if (moleculeGroup) {
    moleculeGroup.rotation.x = moleculeRotationX
    moleculeGroup.rotation.y = moleculeRotationY
    moleculeGroup.scale.setScalar(moleculeScale)
    // 整体上下浮动
    moleculeGroup.position.y = floatOffset
  }

  // 检查分子结构是否渲染完成
  let allParticlesInPlace = true

  for (let i = 0; i < originalParticleCount; i++) {
    const target = moleculeTargets[i]
    const px = positions[i * 3]
    const py = positions[i * 3 + 1]
    const pz = positions[i * 3 + 2]

    let targetX: number, targetY: number, targetZ: number

    if (
      isTransitioning &&
      (transitionState === 'scatter' || transitionState === 'pause')
    ) {
      const scatter = scatterTargets[i]
      const startPos = scatterStartPositions[i] || { x: px, y: py, z: pz }
      const progress = transitionState === 'scatter' ? transitionProgress : 1.0

      targetX = startPos.x + (scatter.x - startPos.x) * progress
      targetY = startPos.y + (scatter.y - startPos.y) * progress
      targetZ = startPos.z + (scatter.z - startPos.z) * progress
    } else {
      // 粒子目标位置（Group会处理旋转，这里只需要本地坐标）
      targetX = target.x
      targetY = target.y
      targetZ = target.z
    }

    const dx = targetX - px
    const dy = targetY - py
    const dz = targetZ - pz
    const distToTarget = Math.sqrt(dx * dx + dy * dy + dz * dz)

    if (distToTarget < 0.05) {
      positions[i * 3] = targetX
      positions[i * 3 + 1] = targetY
      positions[i * 3 + 2] = targetZ
    } else {
      // 匀速汇聚，添加基于粒子索引的微小延迟让汇聚更有层次
      const delay = (i % 100) * 0.0001
      const speed = 0.04 + delay

      positions[i * 3] += dx * speed
      positions[i * 3 + 1] += dy * speed
      positions[i * 3 + 2] += dz * speed
      allParticlesInPlace = false
    }
  }

  // 汇聚开始0.1秒后启动自动旋转
  const timeSinceConverge = (Date.now() - convergeStartTime) / 1000
  if (
    convergeStartTime > 0 &&
    timeSinceConverge >= 0.1 &&
    !autoRotationActive &&
    !isDragging
  ) {
    autoRotationActive = true
  }

  // 如果所有粒子都到位，标记完成
  if (allParticlesInPlace && !moleculeFormationComplete) {
    moleculeFormationComplete = true
  }

  particles.geometry.attributes.position.needsUpdate = true

  // 背景粒子汇聚动画和浮动效果（每个粒子独立浮动）
  if (bgParticles && bgTargetPositions.length > 0) {
    const bgPositions = bgParticles.geometry.attributes.position
      .array as Float32Array
    const bgCount = bgPositions.length / 3
    const time = Date.now() * 0.001

    for (let i = 0; i < bgCount; i++) {
      const target = bgTargetPositions[i]
      if (!target) continue

      const px = bgPositions[i * 3]
      const py = bgPositions[i * 3 + 1]
      const pz = bgPositions[i * 3 + 2]

      // 每个粒子独立的浮动参数
      const floatSpeed = 0.5 + (i % 10) * 0.08
      const floatAmplitude = 0.2 + (i % 5) * 0.1
      const floatPhase = i * 0.2

      // 目标Y位置包含独立的浮动偏移
      const floatOffsetY =
        Math.sin(time * floatSpeed + floatPhase) * floatAmplitude
      const targetY = target.y + floatOffsetY

      const dx = target.x - px
      const dy = targetY - py
      const dz = target.z - pz

      // 平滑移动到目标位置（包含浮动）
      const speed = 0.08
      bgPositions[i * 3] += dx * speed
      bgPositions[i * 3 + 1] += dy * speed
      bgPositions[i * 3 + 2] += dz * speed
    }

    bgParticles.geometry.attributes.position.needsUpdate = true
  }
}

function updateLongPressEffect() {
  if (!isMouseDown || currentShape !== 'molecule') {
    return
  }

  const holdDuration = (Date.now() - mouseDownTime) / 1000

  if (holdDuration > 0.3 && !clickEffectActive) {
    clickEffectActive = true
    clickEffectProgress = 0

    const positions = particles.geometry.attributes.position
      .array as Float32Array
    originalPositions = []
    for (let i = 0; i < moleculeTargets.length; i++) {
      originalPositions.push({
        x: positions[i * 3],
        y: positions[i * 3 + 1],
        z: positions[i * 3 + 2],
      })
    }
  }
}

function startTransitionToFourthImage() {
  if (fourthImageTargets.length === 0) {
    return
  }

  currentImageIndex = 4

  isTransitioning = true
  transitionState = 'scatter'
  transitionProgress = 0

  scatterStartPositions = []
  const currentPositions = particles.geometry.attributes.position
    .array as Float32Array
  const originalParticleCount = moleculeTargets.length

  for (let i = 0; i < originalParticleCount; i++) {
    scatterStartPositions.push({
      x: currentPositions[i * 3],
      y: currentPositions[i * 3 + 1],
      z: currentPositions[i * 3 + 2],
    })
  }

  scatterTargets = []
  for (let i = 0; i < originalParticleCount; i++) {
    const startPos = scatterStartPositions[i]

    let dirX = startPos.x
    let dirY = startPos.y
    const distance = Math.sqrt(dirX * dirX + dirY * dirY)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirY = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
    }

    const flyDistance = 20 + Math.random() * 30
    scatterTargets.push({
      x: startPos.x + dirX * flyDistance,
      y: startPos.y + dirY * flyDistance,
      z: (Math.random() - 0.5) * 40,
    })
  }
}

function startTransitionToThirdImage() {
  if (thirdImageTargets.length === 0) {
    return
  }

  currentImageIndex = 3

  isTransitioning = true
  transitionState = 'scatter'
  transitionProgress = 0

  scatterStartPositions = []
  const currentPositions = particles.geometry.attributes.position
    .array as Float32Array
  const originalParticleCount = moleculeTargets.length

  for (let i = 0; i < originalParticleCount; i++) {
    scatterStartPositions.push({
      x: currentPositions[i * 3],
      y: currentPositions[i * 3 + 1],
      z: currentPositions[i * 3 + 2],
    })
  }

  scatterTargets = []
  for (let i = 0; i < originalParticleCount; i++) {
    const startPos = scatterStartPositions[i]

    let dirX = startPos.x
    let dirY = startPos.y
    const distance = Math.sqrt(dirX * dirX + dirY * dirY)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirY = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
    }

    const flyDistance = 20 + Math.random() * 30
    scatterTargets.push({
      x: startPos.x + dirX * flyDistance,
      y: startPos.y + dirY * flyDistance,
      z: (Math.random() - 0.5) * 40,
    })
  }
}

// 分子点击后扩散并触发城市转场
function startMoleculeScatter() {
  if (currentShape !== 'molecule') return

  isTransitioning = true
  transitionState = 'scatter'
  transitionProgress = 0

  scatterStartPositions = []
  const currentPositions = particles.geometry.attributes.position
    .array as Float32Array
  const originalParticleCount = moleculeTargets.length

  for (let i = 0; i < originalParticleCount; i++) {
    scatterStartPositions.push({
      x: currentPositions[i * 3],
      y: currentPositions[i * 3 + 1],
      z: currentPositions[i * 3 + 2],
    })
  }

  scatterTargets = []
  for (let i = 0; i < originalParticleCount; i++) {
    const startPos = scatterStartPositions[i]

    let dirX = startPos.x
    let dirY = startPos.y
    const distance = Math.sqrt(dirX * dirX + dirY * dirY)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirY = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
    }

    const flyDistance = 12 + Math.random() * 18
    scatterTargets.push({
      x: startPos.x + dirX * flyDistance,
      y: startPos.y + dirY * flyDistance,
      z: (Math.random() - 0.5) * 20,
    })
  }

  // 扩散完成后切换到城市
  setTimeout(() => {
    // 定格 300ms
    setTimeout(() => {
      switchToCity()
    }, 300)
  }, 1200) // 扩散动画 1.2 秒
}

// 城市粒子扩散并切换到分子
let cityScatterStartTime = 0
let isCityScattering = false
let cityScatterTargets: { x: number; y: number; z: number }[] = []
let cityScatterStartPositions: { x: number; y: number; z: number }[] = []
let groundScatterTargets: { x: number; y: number; z: number }[] = []
let groundScatterStartPositions: { x: number; y: number; z: number }[] = []

function startCityScatter() {
  if (currentShape !== 'city' || !cityParticles || !groundParticles) return

  isCityScattering = true
  cityScatterStartTime = Date.now()

  // 记录城市粒子当前位置
  cityScatterStartPositions = []
  cityScatterTargets = []
  const cityPos = cityParticles.geometry.attributes.position
    .array as Float32Array

  for (let i = 0; i < cityTargets.length; i++) {
    const startX = cityPos[i * 3]
    const startY = cityPos[i * 3 + 1]
    const startZ = cityPos[i * 3 + 2]

    cityScatterStartPositions.push({ x: startX, y: startY, z: startZ })

    // 计算扩散方向（从中心向外）
    let dirX = startX
    let dirY = startY
    let dirZ = startZ
    const distance = Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      const phi = Math.random() * Math.PI
      dirX = Math.sin(phi) * Math.cos(angle)
      dirY = Math.sin(phi) * Math.sin(angle)
      dirZ = Math.cos(phi)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
      dirZ = dirZ / distance
    }

    // 扩散距离与圆圈扩散保持一致
    const flyDistance = 12 + Math.random() * 18
    cityScatterTargets.push({
      x: startX + dirX * flyDistance,
      y: startY + dirY * flyDistance,
      z: startZ + dirZ * flyDistance,
    })
  }

  // 记录地面粒子当前位置
  groundScatterStartPositions = []
  groundScatterTargets = []
  const groundPos = groundParticles.geometry.attributes.position
    .array as Float32Array

  for (let i = 0; i < groundTargets.length; i++) {
    const startX = groundPos[i * 3]
    const startY = groundPos[i * 3 + 1]
    const startZ = groundPos[i * 3 + 2]

    groundScatterStartPositions.push({ x: startX, y: startY, z: startZ })

    // 计算扩散方向
    let dirX = startX
    let dirZ = startZ
    const distance = Math.sqrt(dirX * dirX + dirZ * dirZ)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirZ = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirZ = dirZ / distance
    }

    const flyDistance = 15 + Math.random() * 20
    groundScatterTargets.push({
      x: startX + dirX * flyDistance,
      y: startY + (Math.random() - 0.5) * 10,
      z: startZ + dirZ * flyDistance,
    })
  }

  // 扩散完成后切换到分子（1200ms扩散 + 100ms定格）
  setTimeout(() => {
    // 短暂定格后立即切换
    setTimeout(() => {
      switchFromCityToMolecule()
    }, 100)
  }, 1200)
}

function switchFromCityToMolecule() {
  isCityScattering = false
  currentShape = 'molecule'
  currentImageIndex = 2

  // 记录汇聚开始时间
  convergeStartTime = Date.now()
  moleculeFormationComplete = false
  autoRotationActive = false

  // 重置分子缩放和旋转角度为默认值
  moleculeScale = 1.0
  moleculeRotationX = -0.3 // 向上倾斜
  moleculeRotationY = -0.4 // 向左倾斜

  // 先移除旧的背景粒子（城市背景），避免两个背景同时存在
  if (bgParticles) {
    scene.remove(bgParticles)
    bgParticles = null
  }

  // 重置相机位置 - 与 switchToMolecule 保持一致
  // 移动端相机往上移动，让分子结构图显示在屏幕下半部分
  camera.position.z = isMobile ? 15 : 12
  camera.position.x = 0
  camera.position.y = isMobile ? -5 : 0
  camera.lookAt(0, isMobile ? 3 : 0, 0)

  const bgColors = [
    new THREE.Color(0x639ee9),
    new THREE.Color(0x5fcbf2),
    new THREE.Color(0x6cc6ac),
    new THREE.Color(0xa1d5c1),
  ]

  const bgParticleCount = config.bgParticleCount
  const newParticleCount = moleculeTargets.length + bgParticleCount

  const newPositions: number[] = []
  const newColors: number[] = []
  const newIntensities: number[] = []
  const newSpecials: number[] = []

  const aspect = window.innerWidth / window.innerHeight
  const vFov = (camera.fov * Math.PI) / 180
  const height = 2 * Math.tan(vFov / 2) * 15
  const width = height * aspect

  // 为分子粒子生成扩散后的初始位置（与圆圈切换效果一致）
  // 粒子从屏幕周围随机位置向中心汇聚
  scatterTargets = []
  for (let i = 0; i < moleculeTargets.length; i++) {
    // 随机角度，让粒子均匀分布在周围
    const angle = Math.random() * Math.PI * 2
    // 扩散距离与 startTransition 保持一致
    const flyDistance = 20 + Math.random() * 30

    const x = Math.cos(angle) * flyDistance + (Math.random() - 0.5) * 5
    const y = Math.sin(angle) * flyDistance + (Math.random() - 0.5) * 5
    const z = (Math.random() - 0.5) * 40

    newPositions.push(x, y, z)
    scatterTargets.push({ x, y, z })

    const color = moleculeColors[i]
    newColors.push(color.r, color.g, color.b)
    newIntensities.push(moleculeIntensities[i])
    newSpecials.push(moleculeSpecials[i])
  }

  // 创建分子粒子
  const moleculeGeometry = new THREE.BufferGeometry()
  moleculeGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(newPositions, 3),
  )
  moleculeGeometry.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(newColors, 3),
  )
  moleculeGeometry.setAttribute(
    'intensity',
    new THREE.Float32BufferAttribute(newIntensities, 1),
  )
  moleculeGeometry.setAttribute(
    'special',
    new THREE.Float32BufferAttribute(newSpecials, 1),
  )

  const moleculeMaterial = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute vec3 color;
      attribute float intensity;
      varying vec3 vColor;
      varying float vIntensity;

      void main() {
        vColor = color;
        vIntensity = intensity;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = 0.13 * 300.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying float vIntensity;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        float glow = 1.0 - smoothstep(0.0, 0.45, dist);
        glow = pow(glow, 1.0);
        
        float centerBoost = 1.0 - vIntensity;
        centerBoost = pow(centerBoost, 0.6);
        
        float glowStrength = glow * (0.7 + centerBoost * 0.5);
        vec3 glowColor = mix(vColor, vec3(1.0), glowStrength);
        
        glowColor = glowColor * (1.0 + centerBoost * 0.35);
        
        float alpha = 1.0 - smoothstep(0.05, 0.5, dist);
        alpha = pow(alpha, 0.6);
        
        gl_FragColor = vec4(glowColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  particles = new THREE.Points(moleculeGeometry, moleculeMaterial)

  // 创建分子 Group 用于旋转
  moleculeGroup = new THREE.Group()
  moleculeGroup.add(particles)
  scene.add(moleculeGroup)

  // 创建背景粒子
  const bgPositions: number[] = []
  const bgColorsArr: number[] = []
  const bgSizes: number[] = []
  bgTargetPositions = []

  // 移动端需要更大的分布范围来覆盖整个屏幕
  // 移动端竖屏时宽度较小，需要更大的横向乘数
  const bgSpreadMultiplierX = isMobile ? 5.0 : 1.3
  const bgSpreadMultiplierY = isMobile ? 2.0 : 1.3

  for (let i = 0; i < bgParticleCount; i++) {
    const targetX = (Math.random() - 0.5) * width * bgSpreadMultiplierX
    const targetY = (Math.random() - 0.5) * height * bgSpreadMultiplierY
    const targetZ = -3 - Math.random() * 7
    bgTargetPositions.push({ x: targetX, y: targetY, z: targetZ })

    // 从扩散位置开始
    const scatterIndex = i % Math.max(scatterTargets.length, 1)
    if (scatterTargets.length > 0) {
      const scatter = scatterTargets[scatterIndex]
      bgPositions.push(
        scatter.x + (Math.random() - 0.5) * 5,
        scatter.y + (Math.random() - 0.5) * 5,
        scatter.z + (Math.random() - 0.5) * 5,
      )
    } else {
      bgPositions.push(
        (Math.random() - 0.5) * width * 2,
        (Math.random() - 0.5) * height * 2,
        -10 - Math.random() * 10,
      )
    }

    const bgColor = bgColors[Math.floor(Math.random() * bgColors.length)]
    bgColorsArr.push(bgColor.r, bgColor.g, bgColor.b)

    const sizeRand = Math.random()
    if (sizeRand < 0.7) {
      bgSizes.push(0.08 + Math.random() * 0.12)
    } else if (sizeRand < 0.9) {
      bgSizes.push(0.2 + Math.random() * 0.15)
    } else {
      bgSizes.push(0.35 + Math.random() * 0.2)
    }
  }

  const bgGeometry = new THREE.BufferGeometry()
  bgGeometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(bgPositions, 3),
  )
  bgGeometry.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(bgColorsArr, 3),
  )
  bgGeometry.setAttribute('size', new THREE.Float32BufferAttribute(bgSizes, 1))

  const bgMaterial = new THREE.ShaderMaterial({
    uniforms: {
      sizeScale: { value: pixelRatioScale },
      time: { value: 0 },
      clickPos: { value: new THREE.Vector3() },
      clickProgress: { value: 0 },
      clickActive: { value: 0 },
    },
    vertexShader: `
      uniform float sizeScale;
      attribute vec3 color;
      attribute float size;
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform vec3 clickPos;

      void main() {
        vColor = color;
        vPosition = position;
        vDistToClick = distance(position, clickPos);

        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * 300.0 * sizeScale / -mvPosition.z;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying vec3 vPosition;
      varying float vDistToClick;

      uniform float clickProgress;
      uniform float clickActive;

      void main() {
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);

        if (dist > 0.5) discard;

        float alpha = 1.0 - smoothstep(0.3, 0.5, dist);

        vec3 finalColor = vColor;

        if (clickActive > 0.5) {
          float effectRadius = 5.0;
          float influence = 1.0 - clamp(vDistToClick / effectRadius, 0.0, 1.0);

          if (clickProgress < 1.0) {
            float brightness = 1.0 + influence * clickProgress * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * clickProgress * 0.6) * brightness;
          } else {
            float fadeProgress = clickProgress - 1.0;
            float brightness = 1.0 + influence * (1.0 - fadeProgress) * 1.5;
            vec3 glowColor = vec3(1.0, 1.0, 1.0);
            finalColor = mix(vColor, glowColor, influence * (1.0 - fadeProgress) * 0.6) * brightness;
          }
        }

        gl_FragColor = vec4(finalColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  })

  bgParticles = new THREE.Points(bgGeometry, bgMaterial)
  scene.add(bgParticles)

  // 最后移除城市粒子（在分子粒子创建完成后，避免空白）
  if (cityParticles) {
    scene.remove(cityParticles)
    cityParticles = null
  }
  if (groundParticles) {
    scene.remove(groundParticles)
    groundParticles = null
  }
}

function startTransition() {
  // 安全检查
  if (!particles || !particles.geometry) {
    return
  }

  isTransitioning = true
  transitionState = 'scatter'
  transitionProgress = 0

  // 隐藏 logo 图片
  hideLogoImage()

  if (logoParticles) {
    scene.remove(logoParticles)
    logoParticles = null
    showLogo = false
  }

  // 使用分子粒子数量作为目标数量
  const targetParticleCount = moleculeTargets.length

  scatterTargets = []

  scatterStartPositions = []
  const currentPositions = particles.geometry.attributes.position
    .array as Float32Array

  // 记录当前圆圈粒子的位置
  for (let i = 0; i < particleCount; i++) {
    scatterStartPositions.push({
      x: currentPositions[i * 3],
      y: currentPositions[i * 3 + 1],
      z: currentPositions[i * 3 + 2],
    })
  }

  // 为所有分子粒子生成扩散目标位置
  for (let i = 0; i < targetParticleCount; i++) {
    // 如果有对应的圆圈粒子位置，从那里开始扩散
    const startPos = scatterStartPositions[
      i % scatterStartPositions.length
    ] || { x: 0, y: 0, z: 0 }

    let dirX = startPos.x
    let dirY = startPos.y
    const distance = Math.sqrt(dirX * dirX + dirY * dirY)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirY = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
    }

    // 向外扩散的距离 - 减小距离让粒子更集中
    const flyDistance = 5 + Math.random() * 10
    scatterTargets.push({
      x: startPos.x + dirX * flyDistance + (Math.random() - 0.5) * 3,
      y: startPos.y + dirY * flyDistance + (Math.random() - 0.5) * 3,
      z: (Math.random() - 0.5) * 8,
    })
  }
}

// 从圆圈切换到城市（先扩散再汇聚成城市）
let isTransitioningToCity = false
let transitionToCityState = 'idle' // 'scatter' | 'pause' | 'converge'
let transitionToCityProgress = 0
let circleToCityScatterTargets: { x: number; y: number; z: number }[] = []
let circleToCityStartPositions: { x: number; y: number; z: number }[] = []

function startTransitionToCity() {
  if (currentShape !== 'circle') return

  // 安全检查
  if (!particles || !particles.geometry) {
    return
  }

  isTransitioningToCity = true
  transitionToCityState = 'scatter'
  transitionToCityProgress = 0

  // 隐藏 logo 图片
  hideLogoImage()

  if (logoParticles) {
    scene.remove(logoParticles)
    logoParticles = null
    showLogo = false
  }

  // 记录当前圆圈粒子的位置
  circleToCityStartPositions = []
  const currentPositions = particles.geometry.attributes.position
    .array as Float32Array

  for (let i = 0; i < particleCount; i++) {
    circleToCityStartPositions.push({
      x: currentPositions[i * 3],
      y: currentPositions[i * 3 + 1],
      z: currentPositions[i * 3 + 2],
    })
  }

  // 生成扩散目标位置
  circleToCityScatterTargets = []
  for (let i = 0; i < particleCount; i++) {
    const startPos = circleToCityStartPositions[i]

    let dirX = startPos.x
    let dirY = startPos.y
    const distance = Math.sqrt(dirX * dirX + dirY * dirY)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirY = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
    }

    // 扩散距离与切换到分子时一致（5-15范围）
    const flyDistance = 5 + Math.random() * 10
    circleToCityScatterTargets.push({
      x: startPos.x + dirX * flyDistance + (Math.random() - 0.5) * 3,
      y: startPos.y + dirY * flyDistance + (Math.random() - 0.5) * 3,
      z: (Math.random() - 0.5) * 8,
    })
  }

  // 扩散完成后切换到城市
  setTimeout(() => {
    // 短暂定格后立即切换
    setTimeout(() => {
      switchFromCircleToCity()
    }, 100)
  }, 1200)
}

function switchFromCircleToCity() {
  isTransitioningToCity = false
  transitionToCityState = 'idle' // 重置状态
  currentShape = 'city'

  // 移动端城市缩小一点
  cityRotationX = -0.1
  cityRotationY = 0.3
  cityScale = isMobile ? 0.6 : 0.9

  // 移除旧粒子
  if (particles) {
    scene.remove(particles)
    particles = null as any
  }
  if (moleculeGroup) {
    scene.remove(moleculeGroup)
    moleculeGroup = null
  }
  // 移除旧背景粒子（非城市背景）
  if (bgParticles && bgParticles !== preCityBgParticles) {
    scene.remove(bgParticles)
    bgParticles = null
  }

  // 确保预计算数据已初始化
  if (!cityDataInitialized) {
    preRenderCity()
  }

  // 使用预计算的粒子，设置为可见
  if (preCityBgParticles) {
    bgParticles = preCityBgParticles
    bgParticles.visible = true
  }

  // 清空并重新生成初始位置数组
  cityConvergeStartPositions = []
  groundConvergeStartPositions = []

  if (preCityParticles) {
    cityParticles = preCityParticles
    cityParticles.visible = true

    // 更新 uniform 的 sizeScale 值
    const cityMaterial = cityParticles.material as THREE.ShaderMaterial
    if (cityMaterial.uniforms && cityMaterial.uniforms.sizeScale) {
      cityMaterial.uniforms.sizeScale.value = particleSizeScale
    }

    const cityPos = cityParticles.geometry.attributes.position
      .array as Float32Array

    // 设置初始扩散位置并保存
    for (let i = 0; i < cityTargets.length; i++) {
      const angle = Math.random() * Math.PI * 2
      const dist = 30 + Math.random() * 50
      const x = Math.cos(angle) * dist
      const y = Math.random() * 30 - 15
      const z = Math.sin(angle) * dist
      cityPos[i * 3] = x
      cityPos[i * 3 + 1] = y
      cityPos[i * 3 + 2] = z
      cityConvergeStartPositions.push({ x, y, z })
    }
    cityParticles.geometry.attributes.position.needsUpdate = true

    cityParticles.rotation.x = cityRotationX
    cityParticles.rotation.y = cityRotationY
    cityParticles.scale.setScalar(cityScale)
  }

  if (preGroundParticles) {
    groundParticles = preGroundParticles
    groundParticles.visible = true

    // 更新 uniform 的 sizeScale 值
    const groundMaterial = groundParticles.material as THREE.ShaderMaterial
    if (groundMaterial.uniforms && groundMaterial.uniforms.sizeScale) {
      groundMaterial.uniforms.sizeScale.value = particleSizeScale
    }

    const groundPos = groundParticles.geometry.attributes.position
      .array as Float32Array

    // 设置初始扩散位置并保存
    for (let i = 0; i < groundTargets.length; i++) {
      const angle = Math.random() * Math.PI * 2
      const dist = 50 + Math.random() * 80
      const x = Math.cos(angle) * dist
      const y = Math.random() * 20 - 10
      const z = Math.sin(angle) * dist
      groundPos[i * 3] = x
      groundPos[i * 3 + 1] = y
      groundPos[i * 3 + 2] = z
      groundConvergeStartPositions.push({ x, y, z })
    }
    groundParticles.geometry.attributes.position.needsUpdate = true

    groundParticles.rotation.x = cityRotationX
    groundParticles.rotation.y = cityRotationY
    groundParticles.scale.setScalar(cityScale)
  }

  // 调整相机 - 更近更大
  // 移动端相机往上移动，让城市显示在屏幕下半部分
  if (isMobile) {
    camera.position.set(0, 35, 55)
    camera.lookAt(0, 5, 0)
  } else {
    camera.position.set(0, 25, 55)
    camera.lookAt(0, 10, 0)
  }

  // 强制更新相机投影矩阵
  camera.updateProjectionMatrix()

  // 开始汇聚动画
  isCityConverging = true
  cityConvergeStartTime = Date.now()
}

function onMouseMove(event: MouseEvent) {
  // 使用容器的边界矩形来计算正确的鼠标坐标
  // 这样可以处理容器不是全屏或有偏移的情况
  let newMouseX: number
  let newMouseY: number

  if (containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect()
    // 计算鼠标相对于容器的位置，转换为 -1 到 1 的范围
    newMouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1
    newMouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1
  } else {
    // 回退到窗口坐标
    newMouseX = (event.clientX / window.innerWidth) * 2 - 1
    newMouseY = -(event.clientY / window.innerHeight) * 2 + 1
  }

  // 分子模式下的拖动旋转
  if (isDragging && currentShape === 'molecule' && moleculeGroup) {
    const deltaX = event.clientX - previousMousePosition.x
    const deltaY = event.clientY - previousMousePosition.y

    moleculeRotationY += deltaX * 0.01
    moleculeRotationX += deltaY * 0.01

    // 限制X轴旋转角度，防止翻转过度
    moleculeRotationX = Math.max(
      -Math.PI / 2,
      Math.min(Math.PI / 2, moleculeRotationX),
    )

    previousMousePosition.x = event.clientX
    previousMousePosition.y = event.clientY
  }

  // 城市模式下的拖动旋转
  if (isDragging && currentShape === 'city') {
    const deltaX = event.clientX - previousMousePosition.x
    const deltaY = event.clientY - previousMousePosition.y

    cityRotationY += deltaX * 0.005
    cityRotationX += deltaY * 0.005

    // 限制X轴旋转角度
    cityRotationX = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, cityRotationX))

    previousMousePosition.x = event.clientX
    previousMousePosition.y = event.clientY

    // 应用旋转
    if (cityParticles) {
      cityParticles.rotation.x = cityRotationX
      cityParticles.rotation.y = cityRotationY
    }
    if (groundParticles) {
      groundParticles.rotation.x = cityRotationX
      groundParticles.rotation.y = cityRotationY
    }
  }

  // AI模式下的拖动旋转
  if (isDragging && currentShape === 'ai' && aiGroup) {
    const deltaX = event.clientX - previousMousePosition.x
    const deltaY = event.clientY - previousMousePosition.y

    // 球体旋转
    aiRotationY += deltaX * 0.01
    aiRotationX += deltaY * 0.01

    // AI文字也跟随旋转
    aiTextRotationY += deltaX * 0.01
    aiTextRotationX += deltaY * 0.01

    // 限制X轴旋转角度
    aiRotationX = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, aiRotationX))
    aiTextRotationX = Math.max(
      -Math.PI / 2,
      Math.min(Math.PI / 2, aiTextRotationX),
    )

    previousMousePosition.x = event.clientX
    previousMousePosition.y = event.clientY
  }

  mouseMoveVelocity.x = (newMouseX - mouseX) * 10
  mouseMoveVelocity.y = (newMouseY - mouseY) * 10

  mouseX = newMouseX
  mouseY = newMouseY

  // 计算鼠标在3D空间中的位置
  // 使用 raycaster 来获取鼠标射线与 z=0 平面的交点
  const mouseRaycaster = new THREE.Raycaster()
  mouseRaycaster.setFromCamera(new THREE.Vector2(mouseX, mouseY), camera)

  // 计算射线与 z=0 平面的交点
  // 平面方程: z = 0
  // 射线方程: P = origin + t * direction
  const ray = mouseRaycaster.ray
  const planeZ = 0

  // 求解 t: origin.z + t * direction.z = planeZ
  if (Math.abs(ray.direction.z) > 0.0001) {
    const t = (planeZ - ray.origin.z) / ray.direction.z
    mouse3D = ray.origin.clone().add(ray.direction.clone().multiplyScalar(t))
  } else {
    // 如果射线平行于平面，使用旧方法
    const vector = new THREE.Vector3(mouseX, mouseY, 0.5)
    vector.unproject(camera)
    const dir = vector.sub(camera.position).normalize()
    const distance = -camera.position.z / dir.z
    mouse3D = camera.position.clone().add(dir.multiplyScalar(distance))
  }

  // 检测鼠标是否在形状范围内
  let isInsideShape = false

  if (currentShape === 'circle') {
    // 圆圈模式：正圆形检测
    const distToCenter = Math.sqrt(
      mouse3D.x * mouse3D.x + mouse3D.y * mouse3D.y,
    )
    isInsideShape = distToCenter <= circleRadius * 1.2 // 稍微扩大检测范围
  } else if (currentShape === 'molecule') {
    // 分子模式：检测鼠标是否在分子结构图范围内
    // 分子结构图大约在中心区域，使用一个较大的圆形范围检测
    const moleculeRadius = isMobile ? 8 : 12 // 分子结构图的大致半径
    const distToCenter = Math.sqrt(
      mouse3D.x * mouse3D.x + mouse3D.y * mouse3D.y,
    )
    isInsideShape = distToCenter <= moleculeRadius
  } else if (currentShape === 'city') {
    // 城市模式：检测鼠标是否在城市范围内
    // 城市在 3D 空间中，需要考虑相机视角
    // 使用一个较大的矩形范围检测（城市占据大部分屏幕）
    const cityBoundsX = 25 * cityScale
    const cityBoundsY = 35 * cityScale
    isInsideShape =
      Math.abs(mouse3D.x) <= cityBoundsX && Math.abs(mouse3D.y) <= cityBoundsY
  }

  // 更新 hover 状态
  if (isInsideShape && !isHovering) {
    isHovering = true
    emit('hoverChange', true)
  } else if (!isInsideShape && isHovering) {
    isHovering = false
    emit('hoverChange', false)
  }
}

function onMouseDown(event: MouseEvent) {
  if (isTransitioning) return

  isMouseDown = true
  mouseDownTime = Date.now()

  // 城市模式下开始拖动旋转
  if (currentShape === 'city') {
    isDragging = true
    previousMousePosition.x = event.clientX
    previousMousePosition.y = event.clientY
    return
  }

  // AI模式下开始拖动旋转
  if (currentShape === 'ai') {
    isDragging = true
    previousMousePosition.x = event.clientX
    previousMousePosition.y = event.clientY
    // 暂停自动旋转
    aiAutoRotationActive = false
    return
  }

  // 分子模式下开始拖动旋转
  if (currentShape === 'molecule') {
    isDragging = true
    previousMousePosition.x = event.clientX
    previousMousePosition.y = event.clientY
    // 暂停自动旋转
    autoRotationActive = false

    // 如果禁用内部点击，只允许拖动旋转，不触发扩散
    if (!props.disableInternalClick) {
      // 点击分子时发出事件并开始扩散
      startMoleculeScatter()
    }
    return // 分子模式下不触发转场
  }

  // 如果禁用内部点击，不触发圆圈转场
  if (props.disableInternalClick) return

  const raycaster = new THREE.Raycaster()
  const mouse = new THREE.Vector2(mouseX, mouseY)
  raycaster.setFromCamera(mouse, camera)

  const intersects = raycaster.intersectObject(particles)

  if (intersects.length > 0) {
    if (currentShape === 'circle') {
      startTransition()
    }
  }
}

function onMouseUp(_event: MouseEvent) {
  isMouseDown = false
  isDragging = false

  // 松开鼠标后恢复自动旋转（如果分子已形成）
  if (currentShape === 'molecule' && moleculeFormationComplete) {
    autoRotationActive = true
  }

  // AI模式恢复自动旋转
  if (currentShape === 'ai' && aiFormationComplete) {
    aiAutoRotationActive = true
  }

  if (clickEffectActive && clickEffectProgress < 1.0) {
    clickEffectProgress = 1.0
  }
}

// 鼠标滚轮缩放
function onWheel(event: WheelEvent) {
  event.preventDefault()

  const zoomSpeed = 0.1

  if (currentShape === 'molecule') {
    // 分子模式缩放
    if (event.deltaY < 0) {
      moleculeScale = Math.min(maxScale, moleculeScale + zoomSpeed)
    } else {
      moleculeScale = Math.max(minScale, moleculeScale - zoomSpeed)
    }
  } else if (currentShape === 'city') {
    // 城市模式缩放
    if (event.deltaY < 0) {
      cityScale = Math.min(cityMaxScale, cityScale + zoomSpeed)
    } else {
      cityScale = Math.max(cityMinScale, cityScale - zoomSpeed)
    }
    // 应用缩放
    if (cityParticles) {
      cityParticles.scale.setScalar(cityScale)
    }
    if (groundParticles) {
      groundParticles.scale.setScalar(cityScale)
    }
  } else if (currentShape === 'ai') {
    // AI模式缩放
    if (event.deltaY < 0) {
      aiScale = Math.min(aiMaxScale, aiScale + zoomSpeed)
    } else {
      aiScale = Math.max(aiMinScale, aiScale - zoomSpeed)
    }
  }
}

// 触摸事件处理 - 移动端支持
function onTouchStart(event: TouchEvent) {
  if (isTransitioning) return

  const touch = event.touches[0]

  // 使用容器的边界矩形来计算正确的触摸坐标
  if (containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect()
    mouseX = ((touch.clientX - rect.left) / rect.width) * 2 - 1
    mouseY = -((touch.clientY - rect.top) / rect.height) * 2 + 1
  } else {
    mouseX = (touch.clientX / window.innerWidth) * 2 - 1
    mouseY = -(touch.clientY / window.innerHeight) * 2 + 1
  }

  // 更新 mouse3D（与 onMouseMove 保持一致）
  const touchRaycaster = new THREE.Raycaster()
  touchRaycaster.setFromCamera(new THREE.Vector2(mouseX, mouseY), camera)

  const touchRay = touchRaycaster.ray
  const planeZ = 0

  if (Math.abs(touchRay.direction.z) > 0.0001) {
    const t = (planeZ - touchRay.origin.z) / touchRay.direction.z
    mouse3D = touchRay.origin
      .clone()
      .add(touchRay.direction.clone().multiplyScalar(t))
  } else {
    const vector = new THREE.Vector3(mouseX, mouseY, 0.5)
    vector.unproject(camera)
    const dir = vector.sub(camera.position).normalize()
    const distance = -camera.position.z / dir.z
    mouse3D = camera.position.clone().add(dir.multiplyScalar(distance))
  }

  isMouseDown = true
  mouseDownTime = Date.now()

  // 城市模式下开始拖动旋转
  if (currentShape === 'city') {
    isDragging = true
    previousMousePosition.x = touch.clientX
    previousMousePosition.y = touch.clientY
    return
  }

  // AI模式下开始拖动旋转
  if (currentShape === 'ai') {
    isDragging = true
    previousMousePosition.x = touch.clientX
    previousMousePosition.y = touch.clientY
    aiAutoRotationActive = false
    return
  }

  // 分子模式下开始拖动旋转
  if (currentShape === 'molecule') {
    isDragging = true
    previousMousePosition.x = touch.clientX
    previousMousePosition.y = touch.clientY
    // 暂停自动旋转
    autoRotationActive = false
    return // 分子模式下不触发转场
  }

  // 如果禁用内部点击，不触发圆圈转场
  if (props.disableInternalClick) return

  const raycaster = new THREE.Raycaster()
  const mouse = new THREE.Vector2(mouseX, mouseY)
  raycaster.setFromCamera(mouse, camera)

  const intersects = raycaster.intersectObject(particles)

  if (intersects.length > 0) {
    if (currentShape === 'circle') {
      startTransition()
    }
  }
}

function onTouchMove(event: TouchEvent) {
  const touch = event.touches[0]

  // 使用容器的边界矩形来计算正确的触摸坐标
  let newMouseX: number
  let newMouseY: number

  if (containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect()
    newMouseX = ((touch.clientX - rect.left) / rect.width) * 2 - 1
    newMouseY = -((touch.clientY - rect.top) / rect.height) * 2 + 1
  } else {
    newMouseX = (touch.clientX / window.innerWidth) * 2 - 1
    newMouseY = -(touch.clientY / window.innerHeight) * 2 + 1
  }

  // 城市模式下的拖动旋转
  if (isDragging && currentShape === 'city') {
    const deltaX = touch.clientX - previousMousePosition.x
    const deltaY = touch.clientY - previousMousePosition.y

    cityRotationY += deltaX * 0.005
    cityRotationX += deltaY * 0.005
    cityRotationX = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, cityRotationX))

    previousMousePosition.x = touch.clientX
    previousMousePosition.y = touch.clientY

    if (cityParticles) {
      cityParticles.rotation.x = cityRotationX
      cityParticles.rotation.y = cityRotationY
    }
    if (groundParticles) {
      groundParticles.rotation.x = cityRotationX
      groundParticles.rotation.y = cityRotationY
    }
  }

  // AI模式下的拖动旋转
  if (isDragging && currentShape === 'ai' && aiGroup) {
    const deltaX = touch.clientX - previousMousePosition.x
    const deltaY = touch.clientY - previousMousePosition.y

    aiRotationY += deltaX * 0.01
    aiRotationX += deltaY * 0.01
    aiRotationX = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, aiRotationX))

    previousMousePosition.x = touch.clientX
    previousMousePosition.y = touch.clientY
  }

  // 分子模式下的拖动旋转
  if (isDragging && currentShape === 'molecule' && moleculeGroup) {
    const deltaX = touch.clientX - previousMousePosition.x
    const deltaY = touch.clientY - previousMousePosition.y

    moleculeRotationY += deltaX * 0.01
    moleculeRotationX += deltaY * 0.01

    // 限制X轴旋转角度，防止翻转过度
    moleculeRotationX = Math.max(
      -Math.PI / 2,
      Math.min(Math.PI / 2, moleculeRotationX),
    )

    previousMousePosition.x = touch.clientX
    previousMousePosition.y = touch.clientY
  }

  mouseMoveVelocity.x = (newMouseX - mouseX) * 10
  mouseMoveVelocity.y = (newMouseY - mouseY) * 10

  mouseX = newMouseX
  mouseY = newMouseY

  // 计算鼠标在3D空间中的位置（与 onMouseMove 保持一致）
  const touchMoveRaycaster = new THREE.Raycaster()
  touchMoveRaycaster.setFromCamera(new THREE.Vector2(mouseX, mouseY), camera)

  const touchMoveRay = touchMoveRaycaster.ray
  const planeZ = 0

  if (Math.abs(touchMoveRay.direction.z) > 0.0001) {
    const t = (planeZ - touchMoveRay.origin.z) / touchMoveRay.direction.z
    mouse3D = touchMoveRay.origin
      .clone()
      .add(touchMoveRay.direction.clone().multiplyScalar(t))
  } else {
    const vector = new THREE.Vector3(mouseX, mouseY, 0.5)
    vector.unproject(camera)
    const dir = vector.sub(camera.position).normalize()
    const distance = -camera.position.z / dir.z
    mouse3D = camera.position.clone().add(dir.multiplyScalar(distance))
  }
}

function onTouchEnd(_event: TouchEvent) {
  isMouseDown = false
  isDragging = false

  // 松开触摸后恢复自动旋转（如果分子已形成）
  if (currentShape === 'molecule' && moleculeFormationComplete) {
    autoRotationActive = true
  }

  // AI模式恢复自动旋转
  if (currentShape === 'ai' && aiFormationComplete) {
    aiAutoRotationActive = true
  }

  if (clickEffectActive && clickEffectProgress < 1.0) {
    clickEffectProgress = 1.0
  }
}

function onResize() {
  if (!camera || !renderer || !containerRef.value) return

  const container = containerRef.value
  const width = container.clientWidth
  const height = container.clientHeight

  camera.aspect = width / height
  camera.updateProjectionMatrix()

  // 手动设置 canvas 尺寸为 CSS 尺寸的 2 倍，确保高清渲染
  const renderWidth = width * fixedPixelRatio
  const renderHeight = height * fixedPixelRatio
  renderer.setSize(renderWidth, renderHeight, false)
  renderer.domElement.style.width = width + 'px'
  renderer.domElement.style.height = height + 'px'
}

// 鼠标进入容器 - 不再直接触发 hover，由 onMouseMove 检测圆圈范围
function onMouseEnter() {
  // hover 检测已移到 onMouseMove 中，根据鼠标是否在圆圈范围内判断
}

// 鼠标离开容器
function onMouseLeave() {
  // 鼠标离开容器时，重置 hover 状态
  if (isHovering) {
    isHovering = false
    emit('hoverChange', false)
  }
}

onMounted(() => {
  init()

  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mousedown', onMouseDown)
  document.addEventListener('mouseup', onMouseUp)
  // 滚轮缩放 - passive: false 允许 preventDefault
  if (containerRef.value) {
    containerRef.value.addEventListener('wheel', onWheel, { passive: false })
    // 监听鼠标进入和离开容器
    containerRef.value.addEventListener('mouseenter', onMouseEnter)
    containerRef.value.addEventListener('mouseleave', onMouseLeave)
  }
  // 触摸事件支持 - passive: true 允许页面滚动
  document.addEventListener('touchstart', onTouchStart, { passive: true })
  document.addEventListener('touchmove', onTouchMove, { passive: true })
  document.addEventListener('touchend', onTouchEnd)
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  if (animationId) {
    cancelAnimationFrame(animationId)
  }

  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mousedown', onMouseDown)
  document.removeEventListener('mouseup', onMouseUp)
  // 移除滚轮事件和hover事件
  if (containerRef.value) {
    containerRef.value.removeEventListener('wheel', onWheel)
    containerRef.value.removeEventListener('mouseenter', onMouseEnter)
    containerRef.value.removeEventListener('mouseleave', onMouseLeave)
  }
  // 移除触摸事件
  document.removeEventListener('touchstart', onTouchStart)
  document.removeEventListener('touchmove', onTouchMove)
  document.removeEventListener('touchend', onTouchEnd)
  window.removeEventListener('resize', onResize)

  if (renderer) {
    renderer.dispose()
    if (containerRef.value && renderer.domElement) {
      containerRef.value.removeChild(renderer.domElement)
    }
  }

  if (particles) {
    particles.geometry.dispose()
    ;(particles.material as THREE.Material).dispose()
  }

  if (bgParticles) {
    bgParticles.geometry.dispose()
    ;(bgParticles.material as THREE.Material).dispose()
  }

  if (logoParticles) {
    logoParticles.geometry.dispose()
    ;(logoParticles.material as THREE.Material).dispose()
  }

  // 清理AI相关资源
  if (aiSphereParticles) {
    aiSphereParticles.geometry.dispose()
    ;(aiSphereParticles.material as THREE.Material).dispose()
  }

  if (aiTextParticles) {
    aiTextParticles.geometry.dispose()
    ;(aiTextParticles.material as THREE.Material).dispose()
  }
})

// 暴露方法给父组件调用
// triggerMoleculeTransition: 触发圆圈 -> 分子的转场（点击粒子圆圈的效果）
// triggerCityTransition: 触发分子 -> 城市的转场（点击分子结构图的效果）
function triggerMoleculeTransition() {
  if (currentShape === 'circle' && !isTransitioning) {
    startTransition()
  }
}

function triggerCityTransition() {
  if (currentShape === 'molecule' && !isTransitioning) {
    startMoleculeScatter()
  }
}

// 从城市切换到分子
function triggerMoleculeFromCity() {
  if (currentShape === 'city' && !isCityScattering) {
    startCityScatter()
  }
}

// 从圆圈切换到城市
function triggerCityFromCircle() {
  if (currentShape === 'circle' && !isTransitioningToCity && !isTransitioning) {
    startTransitionToCity()
  }
}

// 从分子或城市切换回圆圈
function triggerCircleTransition() {
  if (
    currentShape === 'molecule' &&
    !isTransitioning &&
    !isTransitioningToCircle
  ) {
    // 从分子切换回圆圈 - 先扩散再切换
    startMoleculeToCircleScatter()
  } else if (
    currentShape === 'city' &&
    !isCityScattering &&
    !isTransitioningToCircle
  ) {
    // 从城市切换回圆圈 - 先扩散再切换
    startCityToCircleScatter()
  }
}

// 切换到圆圈的状态变量
let isTransitioningToCircle = false
let toCircleScatterTargets: { x: number; y: number; z: number }[] = []
let toCircleScatterStartPositions: { x: number; y: number; z: number }[] = []

// 从分子扩散并切换到圆圈
function startMoleculeToCircleScatter() {
  if (currentShape !== 'molecule') return

  isTransitioningToCircle = true
  isTransitioning = true
  transitionState = 'scatter'
  transitionProgress = 0

  // 记录当前粒子位置
  toCircleScatterStartPositions = []
  const currentPositions = particles.geometry.attributes.position
    .array as Float32Array
  const particleCount = moleculeTargets.length

  for (let i = 0; i < particleCount; i++) {
    toCircleScatterStartPositions.push({
      x: currentPositions[i * 3],
      y: currentPositions[i * 3 + 1],
      z: currentPositions[i * 3 + 2],
    })
  }

  // 生成扩散目标位置
  toCircleScatterTargets = []
  for (let i = 0; i < particleCount; i++) {
    const startPos = toCircleScatterStartPositions[i]

    let dirX = startPos.x
    let dirY = startPos.y
    const distance = Math.sqrt(dirX * dirX + dirY * dirY)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirY = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
    }

    const flyDistance = 12 + Math.random() * 18
    toCircleScatterTargets.push({
      x: startPos.x + dirX * flyDistance,
      y: startPos.y + dirY * flyDistance,
      z: (Math.random() - 0.5) * 20,
    })
  }

  // 扩散完成后切换到圆圈
  setTimeout(() => {
    setTimeout(() => {
      resetToCircle()
      isTransitioningToCircle = false
    }, 300)
  }, 1200)
}

// 从城市扩散并切换到圆圈
function startCityToCircleScatter() {
  if (currentShape !== 'city' || !cityParticles || !groundParticles) return

  isTransitioningToCircle = true
  isCityScattering = true
  cityScatterStartTime = Date.now()

  // 记录城市粒子当前位置
  cityScatterStartPositions = []
  cityScatterTargets = []
  const cityPos = cityParticles.geometry.attributes.position
    .array as Float32Array

  for (let i = 0; i < cityTargets.length; i++) {
    const startX = cityPos[i * 3]
    const startY = cityPos[i * 3 + 1]
    const startZ = cityPos[i * 3 + 2]

    cityScatterStartPositions.push({ x: startX, y: startY, z: startZ })

    let dirX = startX
    let dirY = startY
    const distance = Math.sqrt(dirX * dirX + dirY * dirY)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirY = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
    }

    const flyDistance = 12 + Math.random() * 18
    cityScatterTargets.push({
      x: startX + dirX * flyDistance,
      y: startY + dirY * flyDistance,
      z: (Math.random() - 0.5) * 20,
    })
  }

  // 记录地面粒子当前位置
  groundScatterStartPositions = []
  groundScatterTargets = []
  const groundPos = groundParticles.geometry.attributes.position
    .array as Float32Array

  for (let i = 0; i < groundTargets.length; i++) {
    const startX = groundPos[i * 3]
    const startY = groundPos[i * 3 + 1]
    const startZ = groundPos[i * 3 + 2]

    groundScatterStartPositions.push({ x: startX, y: startY, z: startZ })

    const angle = Math.random() * Math.PI * 2
    const flyDistance = 15 + Math.random() * 20
    groundScatterTargets.push({
      x: startX + Math.cos(angle) * flyDistance,
      y: startY + (Math.random() - 0.5) * 10,
      z: startZ + Math.sin(angle) * flyDistance,
    })
  }

  // 扩散完成后切换到圆圈
  setTimeout(() => {
    setTimeout(() => {
      resetToCircle()
      isTransitioningToCircle = false
    }, 100)
  }, 1200)
}

// 重置到圆圈状态
function resetToCircle() {
  // 移除当前的粒子系统
  if (moleculeGroup) {
    scene.remove(moleculeGroup)
    moleculeGroup = null
  }
  // 将城市粒子设为不可见（不移除，保持 WebGL 资源状态）
  if (cityParticles) {
    cityParticles.visible = false
  }
  if (groundParticles) {
    groundParticles.visible = false
  }
  // 将城市背景粒子设为不可见
  if (bgParticles === preCityBgParticles && bgParticles) {
    bgParticles.visible = false
  } else if (bgParticles) {
    scene.remove(bgParticles)
    bgParticles = null
  }
  if (particles) {
    scene.remove(particles)
  }

  // 重置状态
  currentShape = 'circle'
  isTransitioning = false
  isTransitioningToCity = false
  transitionToCityState = 'idle' // 重置城市切换状态
  isCityScattering = false
  circleComplete = false
  moleculeFormationComplete = false
  autoRotationActive = false

  // 重置相机位置
  camera.position.z = 20
  camera.position.x = 0
  camera.position.y = isMobile ? -5 : 0
  camera.lookAt(0, isMobile ? 3 : 0, 0)

  // 重新创建圆圈粒子
  createCircleParticles()
}

// 从圆圈切换到AI球体
function startTransitionToAi() {
  if (currentShape !== 'circle') return

  // 安全检查
  if (!particles || !particles.geometry) {
    return
  }

  isTransitioning = true
  transitionState = 'scatter'
  transitionProgress = 0

  // 隐藏 logo 图片
  hideLogoImage()

  if (logoParticles) {
    scene.remove(logoParticles)
    logoParticles = null
    showLogo = false
  }

  // 记录当前圆圈粒子的位置
  scatterStartPositions = []
  const currentPositions = particles.geometry.attributes.position
    .array as Float32Array

  for (let i = 0; i < particleCount; i++) {
    scatterStartPositions.push({
      x: currentPositions[i * 3],
      y: currentPositions[i * 3 + 1],
      z: currentPositions[i * 3 + 2],
    })
  }

  // 生成扩散目标位置
  scatterTargets = []
  for (let i = 0; i < particleCount; i++) {
    const startPos = scatterStartPositions[i]

    let dirX = startPos.x
    let dirY = startPos.y
    const distance = Math.sqrt(dirX * dirX + dirY * dirY)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirY = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
    }

    const flyDistance = 5 + Math.random() * 10
    scatterTargets.push({
      x: startPos.x + dirX * flyDistance + (Math.random() - 0.5) * 3,
      y: startPos.y + dirY * flyDistance + (Math.random() - 0.5) * 3,
      z: (Math.random() - 0.5) * 8,
    })
  }

  // 扩散完成后切换到AI
  setTimeout(() => {
    setTimeout(() => {
      switchFromCircleToAi()
    }, 100)
  }, 1200)
}

function switchFromCircleToAi() {
  isTransitioning = false
  transitionState = 'idle'

  // 移除旧粒子
  if (particles) {
    scene.remove(particles)
    particles = null as any
  }
  if (bgParticles && bgParticles !== preCityBgParticles) {
    scene.remove(bgParticles)
    bgParticles = null
  }

  // 切换到AI
  switchToAi()
}

// 触发AI转场（从圆圈切换到AI球体）
function triggerAiTransition() {
  if (currentShape === 'circle' && !isTransitioning) {
    startTransitionToAi()
  }
}

// 从AI切换回圆圈
function triggerCircleFromAi() {
  if (currentShape === 'ai' && !isTransitioning) {
    startAiToCircleScatter()
  }
}

// AI扩散并切换到圆圈
function startAiToCircleScatter() {
  if (currentShape !== 'ai' || !aiSphereParticles || !aiTextParticles) return

  isTransitioning = true

  // 记录当前位置
  const spherePos = aiSphereParticles.geometry.attributes.position
    .array as Float32Array
  const aiScatterStartPositions: { x: number; y: number; z: number }[] = []
  const aiScatterTargets: { x: number; y: number; z: number }[] = []

  for (let i = 0; i < aiSphereTargets.length; i++) {
    const startX = spherePos[i * 3]
    const startY = spherePos[i * 3 + 1]
    const startZ = spherePos[i * 3 + 2]

    aiScatterStartPositions.push({ x: startX, y: startY, z: startZ })

    let dirX = startX
    let dirY = startY
    const distance = Math.sqrt(dirX * dirX + dirY * dirY)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirY = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
    }

    const flyDistance = 12 + Math.random() * 18
    aiScatterTargets.push({
      x: startX + dirX * flyDistance,
      y: startY + dirY * flyDistance,
      z: (Math.random() - 0.5) * 20,
    })
  }

  // 执行扩散动画
  const startTime = Date.now()
  const duration = 1200

  const animateScatter = () => {
    const elapsed = Date.now() - startTime
    let progress = Math.min(elapsed / duration, 1)
    progress = 1 - Math.pow(1 - progress, 2.5)

    for (let i = 0; i < aiSphereTargets.length; i++) {
      const start = aiScatterStartPositions[i]
      const target = aiScatterTargets[i]
      const idx = i * 3

      spherePos[idx] = start.x + (target.x - start.x) * progress
      spherePos[idx + 1] = start.y + (target.y - start.y) * progress
      spherePos[idx + 2] = start.z + (target.z - start.z) * progress
    }
    aiSphereParticles!.geometry.attributes.position.needsUpdate = true

    if (progress < 1) {
      requestAnimationFrame(animateScatter)
    } else {
      // 扩散完成，切换到圆圈
      setTimeout(() => {
        cleanupAiAndResetToCircle()
      }, 100)
    }
  }

  animateScatter()
}

function cleanupAiAndResetToCircle() {
  // 移除AI粒子
  if (aiGroup) {
    scene.remove(aiGroup)
    aiGroup = null
  }
  if (aiTextGroup) {
    scene.remove(aiTextGroup)
    aiTextGroup = null
  }
  if (aiSphereParticles) {
    aiSphereParticles.geometry.dispose()
    ;(aiSphereParticles.material as THREE.Material).dispose()
    aiSphereParticles = null
  }
  if (aiTextParticles) {
    aiTextParticles.geometry.dispose()
    ;(aiTextParticles.material as THREE.Material).dispose()
    aiTextParticles = null
  }
  if (bgParticles) {
    scene.remove(bgParticles)
    // 只有当 bgParticles 不是预渲染的城市背景粒子时才 dispose
    if (bgParticles !== preCityBgParticles) {
      if (bgParticles.geometry) bgParticles.geometry.dispose()
      if (bgParticles.material)
        (bgParticles.material as THREE.Material).dispose()
    }
    bgParticles = null
  }

  // 重置AI状态
  aiRotationSpeed = 0
  aiAutoRotationActive = false
  aiFormationComplete = false
  aiConvergeStartTime = 0

  // 重置状态
  currentShape = 'circle'
  isTransitioning = false
  circleComplete = false

  // 重置相机位置
  camera.position.z = 20
  camera.position.x = 0
  camera.position.y = isMobile ? -5 : 0
  camera.lookAt(0, isMobile ? 3 : 0, 0)

  // 重新创建圆圈粒子
  createCircleParticles()
}

// 从AI切换到分子结构图
function triggerMoleculeFromAi() {
  if (currentShape === 'ai' && !isTransitioning) {
    startAiToMoleculeScatter()
  }
}

// AI扩散并切换到分子结构图
function startAiToMoleculeScatter() {
  if (currentShape !== 'ai' || !aiSphereParticles || !aiTextParticles) return

  isTransitioning = true
  aiAutoRotationActive = false

  // 记录当前位置并生成扩散目标
  const spherePos = aiSphereParticles.geometry.attributes.position
    .array as Float32Array
  const aiScatterStartPositions: { x: number; y: number; z: number }[] = []
  const aiScatterTargets: { x: number; y: number; z: number }[] = []

  for (let i = 0; i < aiSphereTargets.length; i++) {
    const startX = spherePos[i * 3]
    const startY = spherePos[i * 3 + 1]
    const startZ = spherePos[i * 3 + 2]

    aiScatterStartPositions.push({ x: startX, y: startY, z: startZ })

    // 向外扩散
    let dirX = startX
    let dirY = startY
    const distance = Math.sqrt(dirX * dirX + dirY * dirY)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirY = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
    }

    const flyDistance = 12 + Math.random() * 18
    aiScatterTargets.push({
      x: startX + dirX * flyDistance,
      y: startY + dirY * flyDistance,
      z: (Math.random() - 0.5) * 20,
    })
  }

  // 保存扩散目标供分子结构图使用
  scatterTargets = aiScatterTargets

  // 执行扩散动画
  const startTime = Date.now()
  const duration = 1000

  const animateScatter = () => {
    const elapsed = Date.now() - startTime
    let progress = Math.min(elapsed / duration, 1)
    progress = 1 - Math.pow(1 - progress, 2.5)

    for (let i = 0; i < aiSphereTargets.length; i++) {
      const start = aiScatterStartPositions[i]
      const target = aiScatterTargets[i]
      const idx = i * 3

      spherePos[idx] = start.x + (target.x - start.x) * progress
      spherePos[idx + 1] = start.y + (target.y - start.y) * progress
      spherePos[idx + 2] = start.z + (target.z - start.z) * progress
    }
    aiSphereParticles!.geometry.attributes.position.needsUpdate = true

    if (progress < 1) {
      requestAnimationFrame(animateScatter)
    } else {
      // 扩散完成，切换到分子结构图
      cleanupAiAndSwitchToMolecule()
    }
  }

  animateScatter()
}

function cleanupAiAndSwitchToMolecule() {
  // 移除AI粒子
  if (aiGroup) {
    scene.remove(aiGroup)
    aiGroup = null
  }
  if (aiTextGroup) {
    scene.remove(aiTextGroup)
    aiTextGroup = null
  }
  if (aiSphereParticles) {
    aiSphereParticles.geometry.dispose()
    ;(aiSphereParticles.material as THREE.Material).dispose()
    aiSphereParticles = null
  }
  if (aiTextParticles) {
    aiTextParticles.geometry.dispose()
    ;(aiTextParticles.material as THREE.Material).dispose()
    aiTextParticles = null
  }
  if (bgParticles) {
    scene.remove(bgParticles)
    // 只有当 bgParticles 不是预渲染的城市背景粒子时才 dispose
    if (bgParticles !== preCityBgParticles) {
      if (bgParticles.geometry) bgParticles.geometry.dispose()
      if (bgParticles.material)
        (bgParticles.material as THREE.Material).dispose()
    }
    bgParticles = null
  }

  // 重置AI状态
  aiRotationSpeed = 0
  aiAutoRotationActive = false
  aiFormationComplete = false
  aiConvergeStartTime = 0

  // 重置状态
  isTransitioning = false

  // 切换到分子结构图
  switchToMolecule()
}

// 从AI切换到城市
function triggerCityFromAi() {
  if (currentShape === 'ai' && !isTransitioning) {
    startAiToCityScatter()
  }
}

// AI扩散并切换到城市
function startAiToCityScatter() {
  if (currentShape !== 'ai' || !aiSphereParticles || !aiTextParticles) return

  isTransitioning = true
  aiAutoRotationActive = false

  // 记录当前位置并生成扩散目标
  const spherePos = aiSphereParticles.geometry.attributes.position
    .array as Float32Array
  const aiScatterStartPositions: { x: number; y: number; z: number }[] = []
  const aiScatterTargets: { x: number; y: number; z: number }[] = []

  for (let i = 0; i < aiSphereTargets.length; i++) {
    const startX = spherePos[i * 3]
    const startY = spherePos[i * 3 + 1]
    const startZ = spherePos[i * 3 + 2]

    aiScatterStartPositions.push({ x: startX, y: startY, z: startZ })

    // 向外扩散
    let dirX = startX
    let dirY = startY
    const distance = Math.sqrt(dirX * dirX + dirY * dirY)

    if (distance < 0.1) {
      const angle = Math.random() * Math.PI * 2
      dirX = Math.cos(angle)
      dirY = Math.sin(angle)
    } else {
      dirX = dirX / distance
      dirY = dirY / distance
    }

    const flyDistance = 12 + Math.random() * 18
    aiScatterTargets.push({
      x: startX + dirX * flyDistance,
      y: startY + dirY * flyDistance,
      z: (Math.random() - 0.5) * 20,
    })
  }

  // 执行扩散动画
  const startTime = Date.now()
  const duration = 1000

  const animateScatter = () => {
    const elapsed = Date.now() - startTime
    let progress = Math.min(elapsed / duration, 1)
    progress = 1 - Math.pow(1 - progress, 2.5)

    for (let i = 0; i < aiSphereTargets.length; i++) {
      const start = aiScatterStartPositions[i]
      const target = aiScatterTargets[i]
      const idx = i * 3

      spherePos[idx] = start.x + (target.x - start.x) * progress
      spherePos[idx + 1] = start.y + (target.y - start.y) * progress
      spherePos[idx + 2] = start.z + (target.z - start.z) * progress
    }
    aiSphereParticles!.geometry.attributes.position.needsUpdate = true

    if (progress < 1) {
      requestAnimationFrame(animateScatter)
    } else {
      // 扩散完成，切换到城市
      cleanupAiAndSwitchToCity()
    }
  }

  animateScatter()
}

function cleanupAiAndSwitchToCity() {
  // 移除AI粒子
  if (aiGroup) {
    scene.remove(aiGroup)
    aiGroup = null
  }
  if (aiTextGroup) {
    scene.remove(aiTextGroup)
    aiTextGroup = null
  }
  if (aiSphereParticles) {
    aiSphereParticles.geometry.dispose()
    ;(aiSphereParticles.material as THREE.Material).dispose()
    aiSphereParticles = null
  }
  if (aiTextParticles) {
    aiTextParticles.geometry.dispose()
    ;(aiTextParticles.material as THREE.Material).dispose()
    aiTextParticles = null
  }
  if (bgParticles) {
    scene.remove(bgParticles)
    // 只有当 bgParticles 不是预渲染的城市背景粒子时才 dispose
    if (bgParticles !== preCityBgParticles) {
      if (bgParticles.geometry) bgParticles.geometry.dispose()
      if (bgParticles.material)
        (bgParticles.material as THREE.Material).dispose()
    }
    bgParticles = null
  }

  // 重置AI状态
  aiRotationSpeed = 0
  aiAutoRotationActive = false
  aiFormationComplete = false
  aiConvergeStartTime = 0

  // 重置状态
  isTransitioning = false

  // 切换到城市
  switchToCity()
}

// 彻底清理所有粒子和状态
function cleanupAll() {
  // 取消动画循环
  if (animationId) {
    cancelAnimationFrame(animationId)
    animationId = 0
  }

  // 清理圆圈粒子
  if (particles) {
    scene.remove(particles)
    if (particles.geometry) particles.geometry.dispose()
    if (particles.material) (particles.material as THREE.Material).dispose()
    particles = null as any
  }

  // 清理分子Group
  if (moleculeGroup) {
    scene.remove(moleculeGroup)
    moleculeGroup = null
  }

  // 清理AI粒子
  if (aiGroup) {
    scene.remove(aiGroup)
    aiGroup = null
  }
  if (aiTextGroup) {
    scene.remove(aiTextGroup)
    aiTextGroup = null
  }
  if (aiSphereParticles) {
    if (aiSphereParticles.geometry) aiSphereParticles.geometry.dispose()
    if (aiSphereParticles.material)
      (aiSphereParticles.material as THREE.Material).dispose()
    aiSphereParticles = null
  }
  if (aiTextParticles) {
    if (aiTextParticles.geometry) aiTextParticles.geometry.dispose()
    if (aiTextParticles.material)
      (aiTextParticles.material as THREE.Material).dispose()
    aiTextParticles = null
  }

  // 清理城市粒子 - 注意：如果是预渲染的粒子，需要同时清理预渲染引用
  if (cityParticles) {
    scene.remove(cityParticles)
    if (cityParticles.geometry) cityParticles.geometry.dispose()
    if (cityParticles.material)
      (cityParticles.material as THREE.Material).dispose()
    // 如果 cityParticles 是预渲染的粒子，清空预渲染引用
    if (cityParticles === preCityParticles) {
      preCityParticles = null
    }
    cityParticles = null
  }
  if (groundParticles) {
    scene.remove(groundParticles)
    if (groundParticles.geometry) groundParticles.geometry.dispose()
    if (groundParticles.material)
      (groundParticles.material as THREE.Material).dispose()
    // 如果 groundParticles 是预渲染的粒子，清空预渲染引用
    if (groundParticles === preGroundParticles) {
      preGroundParticles = null
    }
    groundParticles = null
  }

  // 清理背景粒子
  if (bgParticles) {
    scene.remove(bgParticles)
    if (bgParticles.geometry) bgParticles.geometry.dispose()
    if (bgParticles.material) {
      ;(bgParticles.material as THREE.Material).dispose()
    }
    // 如果 bgParticles 是预渲染的城市背景粒子，清空预渲染引用
    if (bgParticles === preCityBgParticles) {
      preCityBgParticles = null
    }
    bgParticles = null
  }

  // 清理logo粒子
  if (logoParticles) {
    scene.remove(logoParticles)
    logoParticles = null
  }

  // 重置城市数据初始化标志，让下次切换到城市时重新预渲染
  cityDataInitialized = false

  // 重置所有状态
  isTransitioning = false
  transitionState = 'idle'
  transitionProgress = 0
  circleComplete = false
  circleInitTime = 0 // 重置圆圈初始化时间
  showLogo = false

  // 重置 switchTo 扩散状态
  isSwitchToScattering = false
  switchToScatterProgress = 0
  switchToScatterStartPositions = []
  switchToScatterTargets = []
  switchToBgScatterStartPositions = []
  switchToBgScatterTargets = []
  switchToAiTextScatterStartPositions = []
  switchToAiTextScatterTargets = []
  switchToScatterStartTime = 0

  // 重置分子状态
  moleculeFormationComplete = false
  autoRotationActive = false
  moleculeRotationX = -0.3
  moleculeRotationY = -0.4
  moleculeScale = 1.0
  convergeStartTime = 0 // 重置分子汇聚开始时间

  // 重置AI状态
  aiRotationX = 0
  aiRotationY = 0
  aiTextRotationX = 0
  aiTextRotationY = 0
  aiScale = 1.0
  aiFormationComplete = false
  aiAutoRotationActive = false
  aiRotationSpeed = 0
  aiConvergeStartTime = 0
  aiSphereConvergeStartPositions = []
  aiTextConvergeStartPositions = []

  // 重置城市状态
  isCityConverging = false
  cityRotationX = -0.1
  cityRotationY = 0.3
  cityScale = 0.9
  cityConvergeStartPositions = []
  groundConvergeStartPositions = []

  // 重置城市转场状态
  isTransitioningToCity = false
  transitionToCityState = 'idle'
  transitionToCityProgress = 0
  circleToCityScatterTargets = []
  circleToCityStartPositions = []

  // 重置城市扩散状态
  isCityScattering = false
  cityScatterStartTime = 0
  cityScatterTargets = []
  cityScatterStartPositions = []
  groundScatterTargets = []
  groundScatterStartPositions = []

  // 重置切换到圆圈的状态
  isTransitioningToCircle = false
  toCircleScatterTargets = []
  toCircleScatterStartPositions = []

  // 清空城市目标数组，让下次重新生成
  cityTargets = []
  cityColors = []
  cityBgTargetPositions = []
  groundTargets = []
  cityBuildings = [] // 清空建筑物信息

  // 清空目标数组，让下次重新生成
  aiSphereTargets = []
  aiSphereColors = []
  aiTextTargets = []
  aiTextColors = []

  // 清空扩散目标
  scatterTargets = []
  scatterStartPositions = []
  bgTargetPositions = []

  // 清空圆圈位置数组
  circleTargetPositions = []
  circleScatterPositions = []
  circleStartPositions.length = 0

  // 隐藏logo图片
  hideLogoImage()
}

// switchTo 扩散动画状态
let isSwitchToScattering = false
let switchToScatterProgress = 0
let switchToScatterStartPositions: { x: number; y: number; z: number }[] = []
let switchToScatterTargets: { x: number; y: number; z: number }[] = []
let switchToBgScatterStartPositions: { x: number; y: number; z: number }[] = []
let switchToBgScatterTargets: { x: number; y: number; z: number }[] = []
// AI 文字扩散数据
let switchToAiTextScatterStartPositions: { x: number; y: number; z: number }[] =
  []
let switchToAiTextScatterTargets: { x: number; y: number; z: number }[] = []
let switchToTargetShape: 'circle' | 'molecule' | 'city' | 'ai' = 'circle'
let switchToScatterStartTime = 0
const switchToScatterDuration = 600

// 统一的切换函数 - 先扩散当前粒子，再切换到目标形状
function switchTo(target: 'circle' | 'molecule' | 'city' | 'ai') {
  // 如果正在转场中，忽略请求
  if (isTransitioning) return

  // 如果已经是目标状态，忽略请求
  if (currentShape === target) return

  // 标记正在转场
  isTransitioning = true

  // 保存目标形状
  switchToTargetShape = target

  // 根据当前形状执行扩散动画
  if (currentShape === 'circle') {
    // 圆圈扩散动画
    startCircleScatterAndSwitch(target)
  } else if (currentShape === 'molecule') {
    // 分子扩散动画
    startMoleculeScatterAndSwitch(target)
  } else if (currentShape === 'city') {
    // 城市扩散动画
    startCityScatterAndSwitch(target)
  } else if (currentShape === 'ai') {
    // AI扩散动画
    startAiScatterAndSwitch(target)
  }
}

// 圆圈扩散并切换到目标形状
function startCircleScatterAndSwitch(
  targetShape: 'circle' | 'molecule' | 'city' | 'ai',
) {
  if (!particles) {
    // 如果没有粒子，直接切换
    finishSwitchTo(targetShape)
    return
  }

  // 隐藏 logo 图片
  hideLogoImage()

  const positions = particles.geometry.attributes.position.array as Float32Array
  // 使用实际的粒子数量，而不是 particleCount 变量
  const actualParticleCount = positions.length / 3

  // 初始化扩散状态
  switchToScatterStartPositions = []
  switchToScatterTargets = []
  switchToBgScatterStartPositions = []
  switchToBgScatterTargets = []

  // 保存当前位置并生成扩散目标
  for (let i = 0; i < actualParticleCount; i++) {
    switchToScatterStartPositions.push({
      x: positions[i * 3],
      y: positions[i * 3 + 1],
      z: positions[i * 3 + 2],
    })

    // 扩散方向：从圆心向外扩散
    const x = positions[i * 3]
    const y = positions[i * 3 + 1]
    const dist = Math.sqrt(x * x + y * y)
    const flyDist = 10 + Math.random() * 15
    if (dist > 0.1) {
      // 沿着径向方向向外扩散
      switchToScatterTargets.push({
        x: x + (x / dist) * flyDist,
        y: y + (y / dist) * flyDist,
        z: positions[i * 3 + 2] + (Math.random() - 0.5) * 8,
      })
    } else {
      // 中心附近的粒子随机方向扩散
      const angle = Math.random() * Math.PI * 2
      switchToScatterTargets.push({
        x: Math.cos(angle) * flyDist,
        y: Math.sin(angle) * flyDist,
        z: positions[i * 3 + 2] + (Math.random() - 0.5) * 8,
      })
    }
  }

  // 背景粒子扩散数据
  if (bgParticles && bgParticles.geometry.attributes.position) {
    const bgPos = bgParticles.geometry.attributes.position.array as Float32Array
    const bgCount = bgPos.length / 3

    for (let i = 0; i < bgCount; i++) {
      switchToBgScatterStartPositions.push({
        x: bgPos[i * 3],
        y: bgPos[i * 3 + 1],
        z: bgPos[i * 3 + 2],
      })

      const angle = Math.random() * Math.PI * 2
      const dist = 8 + Math.random() * 15
      switchToBgScatterTargets.push({
        x: bgPos[i * 3] + Math.cos(angle) * dist,
        y: bgPos[i * 3 + 1] + Math.sin(angle) * dist,
        z: bgPos[i * 3 + 2] + (Math.random() - 0.5) * 8,
      })
    }
  }

  // 启动扩散动画
  isSwitchToScattering = true
  switchToScatterStartTime = Date.now()
  switchToTargetShape = targetShape
}

// 分子扩散并切换到目标形状
function startMoleculeScatterAndSwitch(
  targetShape: 'circle' | 'molecule' | 'city' | 'ai',
) {
  if (!particles || !moleculeGroup) {
    finishSwitchTo(targetShape)
    return
  }

  const positions = particles.geometry.attributes.position.array as Float32Array
  const count = positions.length / 3

  // 初始化扩散状态
  switchToScatterStartPositions = []
  switchToScatterTargets = []
  switchToBgScatterStartPositions = []
  switchToBgScatterTargets = []

  for (let i = 0; i < count; i++) {
    switchToScatterStartPositions.push({
      x: positions[i * 3],
      y: positions[i * 3 + 1],
      z: positions[i * 3 + 2],
    })

    const angle = Math.random() * Math.PI * 2
    const dist = 5 + Math.random() * 10
    switchToScatterTargets.push({
      x: positions[i * 3] + Math.cos(angle) * dist,
      y: positions[i * 3 + 1] + Math.sin(angle) * dist,
      z: positions[i * 3 + 2] + (Math.random() - 0.5) * 5,
    })
  }

  // 背景粒子扩散数据
  if (bgParticles && bgParticles.geometry.attributes.position) {
    const bgPos = bgParticles.geometry.attributes.position.array as Float32Array
    const bgCount = bgPos.length / 3

    for (let i = 0; i < bgCount; i++) {
      switchToBgScatterStartPositions.push({
        x: bgPos[i * 3],
        y: bgPos[i * 3 + 1],
        z: bgPos[i * 3 + 2],
      })

      const angle = Math.random() * Math.PI * 2
      const dist = 8 + Math.random() * 15
      switchToBgScatterTargets.push({
        x: bgPos[i * 3] + Math.cos(angle) * dist,
        y: bgPos[i * 3 + 1] + Math.sin(angle) * dist,
        z: bgPos[i * 3 + 2] + (Math.random() - 0.5) * 8,
      })
    }
  }

  // 启动扩散动画
  isSwitchToScattering = true
  switchToScatterStartTime = Date.now()
  switchToTargetShape = targetShape
}

// 城市扩散并切换到目标形状
function startCityScatterAndSwitch(
  targetShape: 'circle' | 'molecule' | 'city' | 'ai',
) {
  if (!cityParticles || !groundParticles) {
    finishSwitchTo(targetShape)
    return
  }

  const cityPos = cityParticles.geometry.attributes.position
    .array as Float32Array
  const groundPos = groundParticles.geometry.attributes.position
    .array as Float32Array

  // 初始化扩散状态 - 复用城市扩散变量
  cityScatterStartPositions = []
  cityScatterTargets = []
  groundScatterStartPositions = []
  groundScatterTargets = []

  for (let i = 0; i < cityTargets.length; i++) {
    cityScatterStartPositions.push({
      x: cityPos[i * 3],
      y: cityPos[i * 3 + 1],
      z: cityPos[i * 3 + 2],
    })
    const angle = Math.random() * Math.PI * 2
    const dist = 10 + Math.random() * 20
    cityScatterTargets.push({
      x: cityPos[i * 3] + Math.cos(angle) * dist,
      y: cityPos[i * 3 + 1] + Math.random() * 15,
      z: cityPos[i * 3 + 2] + Math.sin(angle) * dist,
    })
  }

  for (let i = 0; i < groundTargets.length; i++) {
    groundScatterStartPositions.push({
      x: groundPos[i * 3],
      y: groundPos[i * 3 + 1],
      z: groundPos[i * 3 + 2],
    })
    const angle = Math.random() * Math.PI * 2
    const dist = 15 + Math.random() * 25
    groundScatterTargets.push({
      x: groundPos[i * 3] + Math.cos(angle) * dist,
      y: groundPos[i * 3 + 1] + Math.random() * 10,
      z: groundPos[i * 3 + 2] + Math.sin(angle) * dist,
    })
  }

  // 启动扩散动画
  isSwitchToScattering = true
  isCityScattering = true
  switchToScatterStartTime = Date.now()
  cityScatterStartTime = Date.now()
  switchToTargetShape = targetShape
}

// AI扩散并切换到目标形状
function startAiScatterAndSwitch(
  targetShape: 'circle' | 'molecule' | 'city' | 'ai',
) {
  if (!aiSphereParticles) {
    finishSwitchTo(targetShape)
    return
  }

  // 停止AI自动旋转
  aiAutoRotationActive = false

  const spherePos = aiSphereParticles.geometry.attributes.position
    .array as Float32Array
  // 使用实际的粒子数量
  const actualSphereCount = spherePos.length / 3

  // 初始化扩散状态
  switchToScatterStartPositions = []
  switchToScatterTargets = []
  switchToBgScatterStartPositions = []
  switchToBgScatterTargets = []
  switchToAiTextScatterStartPositions = []
  switchToAiTextScatterTargets = []

  for (let i = 0; i < actualSphereCount; i++) {
    switchToScatterStartPositions.push({
      x: spherePos[i * 3],
      y: spherePos[i * 3 + 1],
      z: spherePos[i * 3 + 2],
    })
    // 从球心向外扩散
    const dirX = spherePos[i * 3]
    const dirY = spherePos[i * 3 + 1]
    const dirZ = spherePos[i * 3 + 2]
    const dist = Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ)
    const flyDist = 8 + Math.random() * 12
    if (dist > 0.1) {
      switchToScatterTargets.push({
        x: spherePos[i * 3] + (dirX / dist) * flyDist,
        y: spherePos[i * 3 + 1] + (dirY / dist) * flyDist,
        z: spherePos[i * 3 + 2] + (dirZ / dist) * flyDist,
      })
    } else {
      const angle = Math.random() * Math.PI * 2
      switchToScatterTargets.push({
        x: Math.cos(angle) * flyDist,
        y: Math.sin(angle) * flyDist,
        z: (Math.random() - 0.5) * flyDist,
      })
    }
  }

  // AI 文字粒子扩散数据
  if (aiTextParticles) {
    const textPos = aiTextParticles.geometry.attributes.position
      .array as Float32Array
    const textCount = textPos.length / 3

    for (let i = 0; i < textCount; i++) {
      switchToAiTextScatterStartPositions.push({
        x: textPos[i * 3],
        y: textPos[i * 3 + 1],
        z: textPos[i * 3 + 2],
      })
      // 文字粒子向外扩散
      const angle = Math.random() * Math.PI * 2
      const flyDist = 5 + Math.random() * 10
      switchToAiTextScatterTargets.push({
        x: textPos[i * 3] + Math.cos(angle) * flyDist,
        y: textPos[i * 3 + 1] + Math.sin(angle) * flyDist,
        z: textPos[i * 3 + 2] + (Math.random() - 0.5) * 5,
      })
    }
  }

  // 背景粒子扩散数据
  if (bgParticles && bgParticles.geometry.attributes.position) {
    const bgPos = bgParticles.geometry.attributes.position.array as Float32Array
    const bgCount = bgPos.length / 3

    for (let i = 0; i < bgCount; i++) {
      switchToBgScatterStartPositions.push({
        x: bgPos[i * 3],
        y: bgPos[i * 3 + 1],
        z: bgPos[i * 3 + 2],
      })

      const angle = Math.random() * Math.PI * 2
      const dist = 8 + Math.random() * 15
      switchToBgScatterTargets.push({
        x: bgPos[i * 3] + Math.cos(angle) * dist,
        y: bgPos[i * 3 + 1] + Math.sin(angle) * dist,
        z: bgPos[i * 3 + 2] + (Math.random() - 0.5) * 8,
      })
    }
  }

  // 启动扩散动画
  isSwitchToScattering = true
  switchToScatterStartTime = Date.now()
  switchToTargetShape = targetShape
}

// 完成切换到目标形状
function finishSwitchTo(target: 'circle' | 'molecule' | 'city' | 'ai') {
  // 彻底清理所有粒子和状态
  cleanupAll()

  // 根据目标创建新的形状
  if (target === 'circle') {
    currentShape = 'circle'
    camera.position.set(0, isMobile ? -5 : 0, 20)
    camera.lookAt(0, isMobile ? 3 : 0, 0)
    camera.updateProjectionMatrix()
    isTransitioning = false
    createCircleParticles()
  } else if (target === 'molecule') {
    currentShape = 'circle'
    camera.position.set(0, isMobile ? -5 : 0, 20)
    camera.lookAt(0, isMobile ? 3 : 0, 0)
    camera.updateProjectionMatrix()
    isTransitioning = false
    switchToMolecule()
  } else if (target === 'city') {
    currentShape = 'circle'
    isTransitioning = false
    switchToCity()
  } else if (target === 'ai') {
    currentShape = 'circle'
    camera.position.set(0, isMobile ? -5 : 0, isMobile ? 18 : 15)
    camera.lookAt(0, isMobile ? 3 : 0, 0)
    camera.updateProjectionMatrix()
    isTransitioning = false
    switchToAi()
  }
}

defineExpose({
  switchTo,
})
</script>

<style scoped>
.particle-ring-container {
  width: 100%;
  height: 100%;
  margin: 0;
  background: #ffffff;
  overflow: hidden;
  touch-action: pan-y pinch-zoom;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

@media (max-width: 768px) {
  .particle-ring-container {
    position: relative;
    min-height: 280px;
    max-height: 100%;
  }
}

.particle-ring-container canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

/* Logo 容器 - 居中显示在圆圈中心 */
.logo-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .logo-container {
    top: 62%;
  }
}

.logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-image {
  width: 225px;
  height: auto;
  display: block;
}

@media (max-width: 768px) {
  .logo-image {
    width: 120px;
  }
}
</style>
